import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import usePageTracking from "../../../../hooks/usePageTracking";
import { useTheme } from "../../../../context/ThemeContext";
import {
  activityStart,
  activityEnd,
} from "../../../../redux/slices/trackActivitySlice";
import { getAllStates, getAllCities } from "../../../../redux/slices/authSlice";
import {
  getHospitalData,
  getHospitalDataPublic,
} from "../../../../redux/slices/comparison.Slice";
import {
  getJobDetail,
  getPublicJobDetail,
  getJobsbyStateAndCityData,
  getSimilarJobs,
  jobTracking,
  toggleBookmark,
  checkBookmark,
  appliedJob,
} from "../../../../redux/slices/jobsSlice";
import CreatePostModal from "../../../community/components/create-post-modal/CreatePostModal";
import { usePosts } from "../../../community/hooks";
import { Copy, Send, Users, Edit2 } from "lucide-react";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";
import usePlan from "../../../../hooks/usePlan";
import jobViewService from "../../../../api/jobViewService";
import jobsService from "../../../../api/jobsService";
import { toast } from "sonner";
import Select from "react-select";
import { ReactComponent as FiStar } from "../../../../assets/images/star-icon.svg";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiMapPin,
  FiX,
} from "react-icons/fi";
import { ReactComponent as CrimeIcon } from "../../../../assets/images/shild.svg";
import { ReactComponent as HospitalsIcon } from "../../../../assets/images/hospital-orange.svg";
import { ReactComponent as HomePriceIcon } from "../../../../assets/images/home-trend.svg";
import { ReactComponent as CalculatorIcon } from "../../../../assets/images/mortage-calculator.svg";
import { ReactComponent as MoneyIcon } from "../../../../assets/images/money.svg";
import { ReactComponent as HospitalIcon } from "../../../../assets/images/hospital-orange.svg";
import { ReactComponent as ChevronRightIcon } from "../../../../assets/images/btn-right-aero.svg";
import { ReactComponent as BookmarkCheck } from "../../../../assets/images/bookmarkcheck.svg";
import { ReactComponent as Bookmark } from "../../../../assets/images/bookmark.svg";
import { ReactComponent as JobCalculator } from "../../../../assets/images/job-calculator.svg";
import useTour from "../../../../components/tour/useTour";
import GuidedTour from "../../../../components/tour/GuidedTour";
import getJobDetailTourSteps from "./jobDetailTourSteps";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import {
  Button,
  Card,
  Col,
  Collapse,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import "./jobdetail.scss";
import "./jobDetailCityHeader.scss";
import "../../../search/cityDetailNew.scss";
import { ReactComponent as MapPin } from "../../../../assets/images/location-detail.svg";
import { ReactComponent as Clock } from "../../../../assets/images/clock.svg";
import SEO from "../../../../components/SEO/SEO";
import { Line, Pie, Doughnut } from "react-chartjs-2";
import {
  getSchools,
  checkFilterCityCount,
} from "../../../../redux/slices/searchSlice";
import Jobcards from "../../../search/jobcard";
import CityDetailLimitModal from "../../../restriction-modals/CityDetailLimitModal";
import AuthRequiredModal from "../../../restriction-modals/AuthRequiredModal";

function computeNetMonthlyIncome(salary, taxRate, netAnnualSalary, role) {
  const period = salary?.period?.toLowerCase();
  const min = Number(salary?.min || 0);
  const max = Number(salary?.max || 0);
  const avg = min > 0 || max > 0 ? (min + max) / 2 : 0;
  const rate = Number(taxRate || 0);
  const isRN = String(role || "")
    .toLowerCase()
    .includes("registered nurse");

  if (avg > 0 && period) {
    let grossMonthly = 0;
    switch (period) {
      case "hourly": {
        const hoursPerYear = isRN ? 1872 : 2080;
        grossMonthly = (avg * hoursPerYear) / 12;
        break;
      }
      case "weekly":
        grossMonthly = (avg * 52) / 12;
        break;
      case "monthly":
        grossMonthly = avg;
        break;
      case "yearly":
      case "annual":
        grossMonthly = avg / 12;
        break;
      default:
        break;
    }
    if (grossMonthly > 0) {
      return grossMonthly * (1 - rate);
    }
  }

  // Fallback: net_annual_salary is already after tax
  return Number(netAnnualSalary || 0) / 12;
}

// Returns { annual: grossAnnual, hourly: grossHourly } from the job's posted
// salary. Falls back to city-median API fields when no salary is posted.
function computeGrossDisplay(
  salary,
  medianAnnualSalary,
  grossHourlySalary,
  role,
) {
  const period = salary?.period?.toLowerCase();
  const min = Number(salary?.min || 0);
  const max = Number(salary?.max || 0);
  const avg = min > 0 || max > 0 ? (min + max) / 2 : 0;
  const isRN = String(role || "")
    .toLowerCase()
    .includes("registered nurse");
  const roleHours = isRN ? 1872 : 2080;

  if (avg > 0 && period) {
    let grossAnnual = 0;
    switch (period) {
      case "hourly":
        grossAnnual = avg * roleHours;
        break;
      case "weekly":
        grossAnnual = avg * 52;
        break;
      case "monthly":
        grossAnnual = avg * 12;
        break;
      case "yearly":
      case "annual":
        grossAnnual = avg;
        break;
      default:
        break;
    }
    if (grossAnnual > 0) {
      return { annual: grossAnnual, hourly: grossAnnual / roleHours };
    }
  }

  // Fallback: city median data from API
  return {
    annual: Number(medianAnnualSalary || 0),
    hourly: Number(grossHourlySalary || 0),
  };
}

/* ── Info tooltip — small (i) icon. Self-contained (inline styles) so it
   doesn't depend on jobdetail's stylesheet. Desktop (real mouse) hovers
   in/out; touch devices tap-to-toggle + tap-outside-to-close (hover alone
   flickers open/closed on tap — mobile browsers synthesize a
   mouseenter+mouseleave pair before the click lands). ─────────────────── */
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Tooltip box width — clamped further per-viewport in reposition() below.
const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  // Anchors the popup to the icon's actual screen position (fixed, not
  // relative to the wrapper) and clamps it so it never spills past the
  // left/right edge of the viewport — the old centered-under-icon
  // placement clipped off-screen for icons near a narrow column's edge.
  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({
      left,
      bottom: window.innerHeight - rect.top + 8,
      width,
      arrowLeft,
    });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
        flexShrink: 0,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          width: 14,
          height: 14,
          color: "var(--mmp-text-secondary)",
          cursor: "pointer",
        }}
        aria-label="More info"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShow((s) => !s);
        }}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-5" />
        <path d="M12 8h.01" />
      </svg>
      {show && pos && (
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: pos.left,
            bottom: pos.bottom,
            background: "#0f172a",
            color: "#fff",
            fontSize: 12,
            fontWeight: 400,
            fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
            textTransform: "none",
            letterSpacing: "normal",
            lineHeight: 1.4,
            padding: "8px 10px",
            borderRadius: 8,
            width: pos.width,
            zIndex: 2000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          }}
        >
          {text}
          <div
            style={{
              position: "absolute",
              bottom: -4,
              left: pos.arrowLeft,
              transform: "translateX(-50%) rotate(45deg)",
              width: 8,
              height: 8,
              background: "#0f172a",
            }}
          />
        </div>
      )}
    </span>
  );
};

// Route param may be a bare numeric id (legacy / internal links) or an SEO
// slug ending in the id, e.g. "rn-nurse-1719" — either way the id is the
// trailing hyphen-delimited segment.
const getJobIdFromRouteParam = (param) => {
  if (!param) return param;
  const segments = String(param).split("-");
  return segments[segments.length - 1];
};

const JobDetail = (
  categories,
  categoriesLoading,
  showBookmarks,
  selectedRole,
  selectedJobType,
  selectedShiftType,
) => {
  const { id: routeParam } = useParams();
  const id = getJobIdFromRouteParam(routeParam);
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();

  // Guests (no session) can view this page via a shared link, but every
  // auth-only action/data fetch below is gated behind this flag — see
  // AuthRequiredModal usage further down.
  const isAuthenticated = useSelector((state) =>
    Boolean(state.auth?.user || state.auth?.token),
  );

  // Guided tour — auto-starts on first visit, replayable from the header.
  const jobDetailTourSteps = React.useMemo(() => getJobDetailTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("job-detail");

  // Track Activity — guests skip this, the activity endpoints require auth.
  usePageTracking("JOB_DETAIL", "web", isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    dispatch(activityStart({ page_name: "JOB_DETAIL", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "JOB_DETAIL" }));
  }, [isAuthenticated, dispatch]);
  const navigate = useNavigate();
  const StarIcon = () => <FiStar />;
  const [showMortgageDetails, setShowMortgageDetails] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [preFilledContent, setPreFilledContent] = useState("");
  const [jobListingCategory, setJobListingCategory] = useState(null);
  const {
    JobDetail: authJobDetail,
    PublicJobDetail,
    isDetailLoading,
    hasApplied,
    similarJobs,
    similarJobsLoading,
  } = useSelector((state) => state.jobResults);
  const JobDetail = isAuthenticated ? authJobDetail : PublicJobDetail;
  // getPublicJobDetail doesn't toggle isDetailLoading (it's a separate
  // reducer branch that sets the shared, slice-wide `isLoading` flag
  // instead), so guests track their own fetch-in-flight state locally.
  const [guestDetailLoading, setGuestDetailLoading] = useState(false);
  const isLoading = isAuthenticated ? isDetailLoading : guestDetailLoading;
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalText, setAuthModalText] = useState("");
  const requireAuth = (message) => {
    setAuthModalText(message);
    setShowAuthModal(true);
  };
  const { createPost } = usePosts();
  const hasJobMeta = Boolean(JobDetail?.promoted_by || JobDetail?.source_name);
  const location = useLocation();
  const [activePlan, setActivePlan] = useState("For Sale");
  const [selectedPlan, setSelectedPlan] = useState("For Sale");
  const [distance, setDistance] = useState(10);

  // Sliding pill — For Sale / For Rent toggle
  const forSaleBtnRef = useRef(null);
  const forRentBtnRef = useRef(null);
  const [planSliderStyle, setPlanSliderStyle] = useState({});
  const planHasPlaced = useRef(false);
  useLayoutEffect(() => {
    const btn =
      activePlan === "For Sale" ? forSaleBtnRef.current : forRentBtnRef.current;
    if (btn) {
      setPlanSliderStyle({
        width: btn.offsetWidth + "px",
        transform: `translateX(${btn.offsetLeft}px)`,
        transition: planHasPlaced.current ? undefined : "none",
      });
      planHasPlaced.current = true;
    }
    // JobDetail?.id ensures effect re-runs once the job data loads and the
    // buttons are actually in the DOM (early Spinner return means refs are null
    // during the initial loading render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlan, JobDetail?.id]);
  const schoolsResultsStore = useSelector(
    (state) => state.search.schools || [],
  );

  const city = JobDetail?.city_details?.city_details?.city;
  const state = JobDetail?.city_details?.city_details?.state;

  const [address, setAddress] = useState();
  // Similar jobs — number of cards loaded per "Show more" click / page
  const SIMILAR_LIMIT = 6;
  const [similarPage, setSimilarPage] = useState(1);

  // ── Live Here, Work There (ported from SearchDetail) ──────────────
  const [liveLocOpen, setLiveLocOpen] = useState(false);
  const [liveLocPos, setLiveLocPos] = useState(null);
  const [liveSelectedState, setLiveSelectedState] = useState(null);
  const [liveSelectedCity, setLiveSelectedCity] = useState(null);
  const [liveStateSearch, setLiveStateSearch] = useState("");
  const [liveCitySearch, setLiveCitySearch] = useState("");
  const [liveCitiesLoading, setLiveCitiesLoading] = useState(false);
  const liveLocTriggerRef = useRef(null);
  const liveLocPopoverRef = useRef(null);
  const authStoreForLive = useSelector((state) => state.auth);
  const liveAllStates = authStoreForLive?.stateItems?.states || [];
  const liveAllCities = authStoreForLive?.cityItems?.cities || [];

  useEffect(() => {
    if (!id) return;
    if (isAuthenticated) {
      dispatch(getJobDetail(id));
      return;
    }
    setGuestDetailLoading(true);
    dispatch(getPublicJobDetail(id)).finally(() =>
      setGuestDetailLoading(false),
    );
  }, [id, isAuthenticated, dispatch]);

  // Once the job loads, upgrade the address bar to the SEO slug format
  // (title-id) if it isn't already there — covers links that navigated
  // here with a bare numeric id (e.g. from a notification or dashboard).
  useEffect(() => {
    if (!JobDetail?.id || String(JobDetail.id) !== String(id)) return;

    const titleSlug = String(JobDetail?.title || "")
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    const canonicalParam = titleSlug
      ? `${titleSlug}-${JobDetail.id}`
      : String(JobDetail.id);

    if (routeParam !== canonicalParam) {
      navigate(`/job-detail/${canonicalParam}`, { replace: true });
    }
  }, [JobDetail, id, routeParam, navigate]);

  // Preload states for the Live Here, Work There dropdown.
  useEffect(() => {
    if (!Array.isArray(liveAllStates) || liveAllStates.length === 0) {
      dispatch(getAllStates());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cities for the picked state inside the live dropdown.
  useEffect(() => {
    if (!liveSelectedState) return;
    setLiveCitiesLoading(true);
    Promise.resolve(
      dispatch(getAllCities({ search_state: liveSelectedState.state })),
    ).finally(() => setLiveCitiesLoading(false));
  }, [liveSelectedState, dispatch]);

  // Reset the in-popover search box when switching between state/city views.
  useEffect(() => {
    if (liveSelectedState) {
      setLiveCitySearch("");
    } else {
      setLiveStateSearch("");
    }
  }, [liveSelectedState]);

  // Position the popover next to the trigger, flipping above when below
  // doesn't fit, and cap max-height to the available viewport space so the
  // dropdown never gets clipped against the screen edge.
  useEffect(() => {
    if (!liveLocOpen) return undefined;
    const update = () => {
      if (!liveLocTriggerRef.current) return;
      const rect = liveLocTriggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const PAD = 8;
      const GAP = 6;
      const POPOVER_DEFAULT_MAX_H = 320;

      const width = Math.min(rect.width, vw - 2 * PAD);
      let left = rect.left;
      if (left + width > vw - PAD) left = vw - PAD - width;
      if (left < PAD) left = PAD;

      const spaceBelow = vh - rect.bottom - GAP - PAD;
      const spaceAbove = rect.top - GAP - PAD;
      const wantBelow = Math.min(POPOVER_DEFAULT_MAX_H, spaceBelow);
      const wantAbove = Math.min(POPOVER_DEFAULT_MAX_H, spaceAbove);

      let top;
      let maxHeight;
      if (spaceBelow >= POPOVER_DEFAULT_MAX_H || spaceBelow >= spaceAbove) {
        top = rect.bottom + GAP;
        maxHeight = Math.max(120, wantBelow);
      } else {
        maxHeight = Math.max(120, wantAbove);
        top = rect.top - GAP - maxHeight;
      }

      setLiveLocPos({ top, left, width, maxHeight });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [liveLocOpen]);

  // Outside-click + Escape to close the popover.
  useEffect(() => {
    if (!liveLocOpen) return undefined;
    const onDown = (e) => {
      if (
        liveLocTriggerRef.current &&
        liveLocTriggerRef.current.contains(e.target)
      )
        return;
      if (
        liveLocPopoverRef.current &&
        liveLocPopoverRef.current.contains(e.target)
      )
        return;
      setLiveLocOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setLiveLocOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [liveLocOpen]);

  // Record + fetch job-view usage so the banner shows accurate counts.
  // record() is idempotent (same job/day = no extra charge), so it's safe
  // to call on mount even when SimpleJobCard already recorded on Apply
  // click — and it covers direct-URL access too. 403 → block + show modal.
  useEffect(() => {
    if (!id || !isAuthenticated) return;
    let cancelled = false;
    jobViewService
      .record(Number(id))
      .then((res) => {
        if (cancelled) return;
        setJobViewUsage(res?.data?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const body = err?.response?.data;
        if (
          err?.response?.status === 403 &&
          body?.errorType === "JOB_VIEW_LIMIT_REACHED"
        ) {
          setJobViewUsage(body?.data || null);
          const limitData = {
            planSlug: body?.data?.plan_slug || "free",
            used: body?.data?.used ?? 5,
            limit: body?.data?.limit ?? 5,
          };
          jobViewLimitDataRef.current = limitData;
          setJobViewLimitInfo(limitData);
        }
        // Other errors: leave banner hidden rather than misreport.
      });
    return () => {
      cancelled = true;
    };
  }, [id, isAuthenticated]);

  // Fetch similar jobs (first page) whenever the job changes. This endpoint
  // (frontend/jobs/:id/similar) doesn't require auth, so guests get results
  // too — clicking through to one is what's gated (see similar-job-card
  // onClick below).
  useEffect(() => {
    if (id) {
      setSimilarPage(1);
      dispatch(getSimilarJobs({ id, page: 1, limit: SIMILAR_LIMIT }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  const handleLoadMoreSimilar = () => {
    const nextPage = similarPage + 1;
    setSimilarPage(nextPage);
    dispatch(getSimilarJobs({ id, page: nextPage, limit: SIMILAR_LIMIT }));
  };

  // ── Similar job card display helpers ──────────────────────────────
  const similarAvatarColors = [
    "054473",
    "0195d3",
    "7c3aed",
    "0b5c8e",
    "007a53",
  ];

  const getSimilarCompany = (job) =>
    job?.company?.name || job?.company_name || job?.employer_name || "Company";

  // Background color (hex without #) for the ui-avatars fallback image
  const getSimilarAvatarBg = (job) => {
    const key = String(job?.id ?? getSimilarCompany(job));
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    return similarAvatarColors[Math.abs(hash) % similarAvatarColors.length];
  };

  // Use the job's profile_photo if present, else a ui-avatars fallback
  const getSimilarAvatarSrc = (job) => {
    const photo = job?.profile_photo;
    if (photo) {
      return photo.startsWith("http")
        ? photo
        : `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${photo}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      getSimilarCompany(job),
    )}&background=${getSimilarAvatarBg(job)}&color=fff&size=100&bold=true`;
  };

  const hasSimilarSalary = (job) => {
    const salary = job?.salary;
    if (!salary) return false;
    if (typeof salary === "string") return salary.trim().length > 0;
    return Boolean(salary.min) || Boolean(salary.max);
  };

  const formatSimilarSalary = (job) => {
    const salary = job?.salary;
    if (!hasSimilarSalary(job)) return "Salary not listed";
    if (typeof salary === "string") return salary;
    const { min, max, period } = salary;
    const per = period ? `/${period}` : "";
    if (min && max)
      return `$${Number(min).toLocaleString()}–$${Number(
        max,
      ).toLocaleString()}${per}`;
    if (min) return `From $${Number(min).toLocaleString()}${per}`;
    if (max) return `Up to $${Number(max).toLocaleString()}${per}`;
    return "Salary not listed";
  };

  const formatSimilarLeftover = (job) => {
    if (!hasSimilarSalary(job)) return "Salary not listed";
    const value = job?.affordability?.net_monthly_after_rent;
    if (value === null || value === undefined || value === "") return "—";
    return `$${Number(Number(value).toFixed(0)).toLocaleString("en-US")}`;
  };

  // Load application status when page loads for hospital jobs (guests have
  // no application to check).
  useEffect(() => {
    if (isAuthenticated && JobDetail?.id && JobDetail?.source === "hospital") {
      dispatch(appliedJob(JobDetail.id));
    }
  }, [JobDetail?.id, JobDetail?.source, isAuthenticated, dispatch]);
  useEffect(() => {
    if (categories && categories.length > 0) {
      // Find category with ID 9 (Job Listing)
      const jobCategory = categories.find((cat) => cat.id === 9);
      if (jobCategory) {
        setJobListingCategory(jobCategory);
      }
    }
  }, [categories]);

  useEffect(() => {
    if (city && state) {
      const lowerCity = city.toLowerCase();
      const lowerState = state.toLowerCase();

      setAddress(`${lowerCity}, ${lowerState}`);
    }
  }, [city, state]);

  const [showModal, setShowModal] = useState(false);
  const [jobs, setjobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [isJobsExpanded, setIsJobsExpanded] = useState(true);
  const [isHospitalsExpanded, setIsHospitalsExpanded] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState({
    value: "Elementary",
    label: "Elementary",
  });
  const hasTrackedViewRef = React.useRef(false);
  const { from, hospitalData, pathname, searchData, hospitalMapData } =
    location.state || {};
  const store = useSelector((state) => state.comparison);

  const hospitalList = store?.hospitalItems?.data?.hospitals
    ? store?.hospitalItems?.data?.hospitals
    : [];

  // The hospital arrived-via-link (if any) is always the one shown first;
  // otherwise just the first hospital returned for this city.
  const selectedHospital = hospitalData || hospitalMapData;
  const primaryHospital = selectedHospital || hospitalList?.[0];
  // Everything else for this city — revealed only when the card is expanded.
  const otherHospitals = hospitalList.filter(
    (h) => h.id !== primaryHospital?.id,
  );
  const hospitalCountLabel =
    hospitalList.length === 0
      ? "Hospitals"
      : hospitalList.length === 1
      ? "1 Hospital"
      : `${hospitalList.length} Hospitals`;

  // Clicking a hospital opens its careers page in a new tab. No-op if the
  // hospital has no careers_url on file.
  const handleHospitalCareersClick = (careersUrl) => {
    if (!careersUrl) return;
    window.open(careersUrl, "_blank", "noopener,noreferrer");
  };

  // Sourced from the already-computed JobDetail (JobDetail/PublicJobDetail
  // depending on auth) rather than the raw redux slot, so this keeps
  // working for guests too.
  const pieArray = JobDetail?.city_details?.pie_data;
  const { schoolsLoading } = useSelector((state) => state.search);
  const SchoolDetails = JobDetail?.city_details?.city_details;

  useEffect(() => {
    const city = JobDetail?.city_details?.city_details?.city;
    const state = JobDetail?.city_details?.city_details?.state;

    if (!city || !state) return;
    if (hasTrackedViewRef.current) return;

    hasTrackedViewRef.current = true;

    const cityName = `${city}, ${state}`;

    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: cityName,
        content_type: "city",
      });
    }
  }, [JobDetail]);

  useEffect(() => {
    const getJobs = async () => {
      if (!city || !state) return;

      setJobsLoading(true);
      try {
        const result = await dispatch(
          getJobsbyStateAndCityData({ city, state }),
        );
        setjobs(result.payload?.data.data || []);
        console.log(result, "jobs result");
      } catch (error) {
        console.error("Failed to fetch jobs:", error);
      } finally {
        setJobsLoading(false);
      }
    };

    getJobs();
  }, [dispatch, city, state]);

  useEffect(() => {
    if (!isAuthenticated || !SchoolDetails?.state || !SchoolDetails?.city) return;

    const payload = {
      state: SchoolDetails.state,
      city: SchoolDetails.city,
      level_codes: selectedLevel.value,
    };

    dispatch(getSchools(payload));
  }, [
    dispatch,
    isAuthenticated,
    selectedLevel,
    SchoolDetails?.state,
    SchoolDetails?.city,
  ]);

  const chartPieData = React.useMemo(() => {
    if (!pieArray || !Array.isArray(pieArray) || pieArray.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = pieArray.map((p) => p.name ?? p.label ?? p.text ?? "");

    const data = pieArray.map((p) => {
      if (typeof p.value === "number") return p.value;
      if (typeof p.val === "number") return p.val;
      if (typeof p.amount === "number") return p.amount;
      if (typeof p.text === "string")
        return Number(p.text.replace(/[^0-9.-]+/g, "")) || 0;
      return 0;
    });

    // Color mapping for specific categories
    const isDark = resolvedTheme === "dark";
    const taxColor = isDark ? "#5B9BC7" : "#015F88";
    const mortgageColor = isDark ? "#3FAEE8" : "#0395D3";
    const leftoverColor = isDark ? "#2A4A5E" : "#A8DBF0";
    const colorMap = {
      "Income Tax": taxColor,
      "income tax": taxColor,
      tax: taxColor,
      taxes: taxColor,
      Mortgage: mortgageColor,
      mortgage: mortgageColor,
      "Left Over": leftoverColor,
      leftover: leftoverColor,
      "left over": leftoverColor,
    };

    const defaultColors = [
      taxColor, // Dark blue for taxes
      mortgageColor, // Medium blue for mortgage
      leftoverColor, // Light blue for leftover
      "#BB63F7",
      "#FFB701",
    ];

    const backgroundColor = pieArray.map((p, i) => {
      // Check if the name/label matches our color map FIRST (priority)
      const itemName = p.name ?? p.label ?? p.text ?? "";

      // Try exact match first
      if (colorMap[itemName]) return colorMap[itemName];

      // Try lowercase match
      const lowerName = itemName.toLowerCase();
      if (colorMap[lowerName]) return colorMap[lowerName];

      // Try partial match for common keywords
      if (lowerName.includes("tax") || lowerName.includes("income")) {
        return taxColor;
      }
      if (lowerName.includes("mortgage")) {
        return mortgageColor;
      }
      if (lowerName.includes("left") || lowerName.includes("leftover")) {
        return leftoverColor;
      }

      // Fall back to default colors by index
      return defaultColors[i % defaultColors.length];
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 0,
        },
      ],
    };
  }, [pieArray, resolvedTheme]);

  const handleShareClick = (e) => {
    e.stopPropagation(); // important
    setShareModal(true);
  };

  const closeShareModal = () => {
    setShareModal(false);
  };

  const [showStickyApply, setShowStickyApply] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // SHOW AFTER USER SCROLLS DOWN
      if (window.scrollY > 300) {
        setShowStickyApply(true);
      } else {
        setShowStickyApply(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const formatSalary = (salary) => {
    if (!salary || (!salary.min && !salary.max)) {
      return "Salary not listed";
    }

    const min = salary.min ? Number(salary.min).toLocaleString() : null;

    const max = salary.max ? Number(salary.max).toLocaleString() : null;

    if (min && max) {
      return `$${min} - $${max}`;
    }

    if (min) {
      return `From $${min}`;
    }

    if (max) {
      return `Up to $${max}`;
    }

    return "Compensation not disclosed";
  };
  const formatbonus = (bonus) => {
    if (!bonus) {
      return "Not listed";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      // Optional: Use 'narrowSymbol' to ensure it just shows $
      currencyDisplay: "symbol",
      // Usually, salaries don't show cents. You can toggle this:
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(bonus);
  };

  const buildJobSeoUrl = (JobDetail) => {
    const titleSlug = createSlug(JobDetail?.title);
    return titleSlug
      ? `/job-detail/${titleSlug}-${JobDetail?.id}`
      : `/job-detail/${JobDetail?.id}`;
  };

  const shareUrl = () => {
    return `${window.location.origin}${buildJobSeoUrl(JobDetail)}`;
  };
  const generateJobPostContent = () => {
    const jobTitle = JobDetail?.title || "Job Opportunity";
    const companyName =
      JobDetail?.company_name || JobDetail?.company?.name || "Company";
    const salary = formatSalary(JobDetail?.salary);

    // Handle both location formats
    const city = JobDetail?.location_city || JobDetail?.location?.city;
    const state = JobDetail?.location_state || JobDetail?.location?.state;
    const location =
      city && state ? `${city}, ${state}` : "Location not specified";

    const applicationUrl = JobDetail?.application_url || "";
    const websiteUrl = `${window.location.origin}/jobs`;

    let content = ` Job Opportunity: ${jobTitle}\n\n`;
    content += ` Company: ${companyName}\n`;
    content += ` Salary: ${salary}\n`;
    content += ` Location: ${location}\n`;

    if (JobDetail?.work_model) {
      content += ` Work Mode: ${JobDetail.work_model}\n`;
    }
    if (JobDetail?.job_type) {
      content += ` Job Type: ${JobDetail.job_type}\n`;
    }
    if (JobDetail?.shift_type) {
      content += ` Shift: ${JobDetail.shift_type}\n`;
    }

    if (JobDetail?.description) {
      content += `\n Description:\n${JobDetail.description}\n`;
    }

    if (applicationUrl) {
      content += `\nApply here: ${applicationUrl}\n`;
    }
    return content;
  };

  const timeAgo = (date) => {
    if (!date) return "";

    const now = Date.now();
    const past = new Date(date).getTime();
    const diffMs = now - past;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    // Just now
    if (seconds < 60) return "Just now";

    // Minutes
    if (minutes < 60) return minutes === 1 ? "1 min ago" : `${minutes} min ago`;

    // Hours
    if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

    // Weeks (ONLY exact multiples, up to 4 weeks)
    if (days >= 7 && days <= 28 && days % 7 === 0) {
      const weeks = days / 7;
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    }

    // Days (everything else under 30)
    if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;

    // Months
    if (months < 12)
      return months === 1 ? "1 month ago" : `${months} months ago`;

    // Years
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };
  const getApplyUrl = (url) => {
    if (!url) return "#";

    // If already has http or https, return as is
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    // Otherwise, prepend https
    return `https://${url}`;
  };

  const shareToSocialMedia = (platform) => {
    const applicationUrl = shareUrl();

    const jobTitle = JobDetail?.title || "this job opportunity";
    const companyName =
      JobDetail?.company_name || JobDetail?.company?.name || "Company";

    const shareText = `Check out ${jobTitle} at ${companyName}`;

    let url = "";

    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(applicationUrl)}`;
        break;

      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;

      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + applicationUrl,
        )}`;
        break;

      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;

      case "copy":
        navigator.clipboard
          .writeText(applicationUrl)
          .then(() => toast.success("Link copied to clipboard!"))
          .catch(() => toast.error("Failed to copy link"));
        closeShareModal();
        return;

      default:
        return;
    }

    if (url) {
      window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      closeShareModal();
    }
  };

  const avatarColors = [
    // '0195D3',
    // '16A34A',
    // 'DC2626',
    // '7C3AED',
    // 'EA580C',
    // '0D9488',
    // 'CA8A04',
    "054473",
    "0195D3",
    "7c3aed",
  ];

  const getAvatarColor = (key) => {
    let hash = 0;
    const str = String(key || "Company");

    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  const avatarBg = getAvatarColor(
    JobDetail?.id || JobDetail?.company_name || JobDetail?.company?.name,
  );

  const createSlug = (text = "") => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };
  // const handleBookmark = (jobId) => {
  //   dispatch(toggleBookmark({ job_id: jobId }));
  // };
  const handleBookmark = async (jobId) => {
    if (!isAuthenticated) {
      requireAuth("Log in or sign up to save this job.");
      return;
    }
    try {
      const res = await dispatch(toggleBookmark({ job_id: jobId })).unwrap();

      const isBookmarked = res?.data?.bookmarked;
      // Keep the local API-check result in sync so the bookmark icon
      // flips right away without waiting for the next check call.
      if (typeof isBookmarked !== "undefined") {
        setBookmarkChecked(Boolean(isBookmarked));
      }

      // ⭐ If we are in bookmark mode and user removed bookmark
      if (showBookmarks && !isBookmarked) {
        // remove card immediately
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("removeBookmarkedJob", { detail: jobId }),
          );
        }, 50);
      }
    } catch (err) {
      if (err?.status === 403) {
        setShowSaveJobModal(true);
      } else {
        console.error("Bookmark toggle failed:", err);
      }
    }
  };

  const distanceOptions = [
    { value: 5, label: "5 mi" },
    { value: 10, label: "10 mi" },
    { value: 15, label: "15 mi" },
    { value: 20, label: "20 mi" },
    { value: 30, label: "30 mi" },
    { value: 50, label: "50 mi" },
  ];

  const getRedfinZipUrl = (zip) => {
    if (!zip) return "https://www.redfin.com";
    return `https://www.redfin.com/zipcode/${zip}`;
  };

  const getZillowLink = async (address, radiusMiles, isRent = false) => {
    const query = address;
    const geoUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      query,
    )}&format=json&limit=1`;

    try {
      const response = await fetch(geoUrl);
      const data = await response.json();

      if (data.length === 0) throw new Error("Location not found");

      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);

      const latOffset = radiusMiles / 69;
      const lonOffset = radiusMiles / (69 * Math.cos((lat * Math.PI) / 180));

      if (isRent) {
        const rentalSearchState = {
          pagination: {},
          mapBounds: {
            north: lat + latOffset,
            south: lat - latOffset,
            east: lon + lonOffset,
            west: lon - lonOffset,
          },
          isMapVisible: true,
          isListVisible: true,
          mapZoom: 11,
          regionSelection: [],
          filterState: {
            fr: { value: true },
            fsba: { value: false },
            fsbo: { value: false },
            nc: { value: false },
            cmsn: { value: false },
            auc: { value: false },
            fore: { value: false },
            sort: { value: "globalrelevance" },
          },
          isMapSearch: true,
        };

        const encodedState = encodeURIComponent(
          JSON.stringify(rentalSearchState),
        );
        const finalUrl = `https://www.zillow.com/homes/for_rent/?searchQueryState=${encodedState}`;
        console.log("Zillow Rentals Link:", finalUrl);
        return finalUrl;
      } else {
        const saleSearchState = {
          pagination: {},
          mapBounds: {
            north: lat + latOffset,
            south: lat - latOffset,
            east: lon + lonOffset,
            west: lon - lonOffset,
          },
          isMapVisible: true,
          isListVisible: true,
          mapZoom: 9,
          filterState: {
            isAllHomes: { value: true },
            sortSelection: { value: "globalrelevance" },
          },
        };

        const encodedState = encodeURIComponent(
          JSON.stringify(saleSearchState),
        );
        const finalUrl = `https://www.zillow.com/homes/for_sale/?searchQueryState=${encodedState}`;
        console.log("Zillow For Sale Link:", finalUrl);
        return finalUrl;
      }
    } catch (error) {
      console.error("Error generating Zillow link:", error);
      return null;
    }
  };
  const [homePrice, setHomePrice] = useState(
    JobDetail?.city_details?.city_details?.home_prices || 472900,
  );
  const [netMonthlySalary, setNetMonthlySalary] = useState(7801);
  // Defaults align with the CSV-derived "Monthly Mortgage Payment (7.065%
  // APR)" column the API exposes (see SearchDetail.js for the full reasoning).
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);

  const [loanTerm, setLoanTerm] = useState(30);

  // Sliding pill — 15 yr / 30 yr loan term toggle
  const term15Ref = useRef(null);
  const term30Ref = useRef(null);
  const [termSliderStyle, setTermSliderStyle] = useState({});
  const termHasPlaced = useRef(false);
  useLayoutEffect(() => {
    // Section is conditionally rendered; reset flag when it closes so
    // the next open snaps the slider into position without animation.
    if (!showMortgageDetails) {
      termHasPlaced.current = false;
      return;
    }
    const btn = loanTerm === 15 ? term15Ref.current : term30Ref.current;
    if (btn) {
      setTermSliderStyle({
        width: btn.offsetWidth + "px",
        transform: `translateX(${btn.offsetLeft}px)`,
        transition: termHasPlaced.current ? undefined : "none",
      });
      termHasPlaced.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanTerm, showMortgageDetails, JobDetail?.id]);

  const [interestRate, setInterestRate] = useState(6.85);

  // Utilities — editable for Pro / free-trial users. Non-Pro users see the
  // same display but get the upgrade modal when they try to edit.
  const DEFAULT_UTILITIES = 500;
  const [affordUtilities, setAffordUtilities] = useState(DEFAULT_UTILITIES);
  const [showUtilitiesEditModal, setShowUtilitiesEditModal] = useState(false);
  const [showUtilitiesUpgradeModal, setShowUtilitiesUpgradeModal] = useState(
    false,
  );
  const [showSaveJobModal, setShowSaveJobModal] = useState(false);
  const [utilitiesDraft, setUtilitiesDraft] = useState(
    String(DEFAULT_UTILITIES),
  );
  const authUser = useSelector((state) => state.auth?.user);
  const profileStore = useSelector((state) => state.profile);

  // Always fetch hospitals for the city being viewed on this job's page.
  // Note: city_details.role is a display string (e.g. "Nurse Midwives"), not
  // the numeric describe_role_id the hospital-info API expects — using it
  // would silently return zero hospitals. Fall back to the viewer's own
  // nursing role id instead, same as SearchDetail.js / Dashboard's
  // HospitalMap widget, and finally to 9 (Registered Nurses) — the same
  // sitewide default Search.js/Dashboard.js use — for guests and any
  // logged-in user who hasn't picked a role yet, so the list still renders
  // instead of silently staying empty.
  const hospitalInfoProfile = profileStore?.profileItem?.data;
  const hospitalInfoRole =
    hospitalInfoProfile?.nursing_role?.id ||
    hospitalInfoProfile?.role ||
    authUser?.nursing_role?.id ||
    authUser?.role ||
    9;
  useEffect(() => {
    if (!city || !state || !hospitalInfoRole) return;
    // /hospital-info requires auth (it also returns the viewer's daily
    // comparison-count usage) — guests hit the public counterpart instead,
    // which returns the same hospital list without that per-user gating.
    dispatch(
      isAuthenticated
        ? getHospitalData({ state, city, role: hospitalInfoRole })
        : getHospitalDataPublic({ state, city, role: hospitalInfoRole }),
    );
  }, [dispatch, city, state, hospitalInfoRole, isAuthenticated]);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser } = usePlan();
  // Plan-cap usage for the "Free plan: X of Y job details viewed today" banner.
  const [jobViewUsage, setJobViewUsage] = useState(null);
  const [jobViewLimitInfo, setJobViewLimitInfo] = useState(null);
  // Persists limit data so re-clicking a job card after modal dismissal still blocks navigation.
  const jobViewLimitDataRef = useRef(null);
  // City detail restriction modal state
  const [cityLimitModalVisible, setCityLimitModalVisible] = useState(false);
  const [cityLimitModalData, setCityLimitModalData] = useState({});
  const [upgradeSource, setUpgradeSource] = useState(null);

  const handleCityDetailClick = async () => {
    if (!isAuthenticated) {
      requireAuth("Log in or sign up to view city details.");
      return;
    }
    const cityId = JobDetail?.city_details?.city_details?.id;
    const planSlug =
      profileStore?.profileItem?.data?.plan?.slug ||
      authUser?.plan?.slug ||
      "free";

    if (planSlug === "free") {
      try {
        const result = await dispatch(
          checkFilterCityCount({
            plan_slug: planSlug,
            increment_count: true,
            city_id: cityId,
          }),
        );
        const restrictionData = result?.payload?.checkFilterCityCountData;

        if (
          restrictionData?.is_comparisan === false &&
          restrictionData?.used_count >= restrictionData?.comparisanMaxCount &&
          restrictionData?.incrementSuccess === false
        ) {
          setUpgradeSource("city_detail_limit");
          setCityLimitModalData({});
          setCityLimitModalVisible(true);
          return;
        }
      } catch (err) {
        console.error("City detail restriction check failed:", err);
      }
    }

    navigate(`/search/details/${cityId}`);
  };

  const openUtilitiesEditor = () => {
    if (!isAuthenticated) {
      requireAuth("Log in or sign up to add expenses for this city.");
      return;
    }
    if (!isProUser) {
      setShowUtilitiesUpgradeModal(true);
      return;
    }
    setUtilitiesDraft(String(affordUtilities));
    setShowUtilitiesEditModal(true);
  };
  const saveUtilitiesDraft = () => {
    const parsed = Number(utilitiesDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setAffordUtilities(DEFAULT_UTILITIES);
    } else {
      setAffordUtilities(parsed);
    }
    setShowUtilitiesEditModal(false);
  };

  const downPaymentAmount = (homePrice * downPaymentPercent) / 100;

  const mortgageAmount = homePrice - downPaymentAmount;

  /* SIMPLE MONTHLY MORTGAGE */

  const monthlyInterest = interestRate / 100 / 12;

  const totalMonths = loanTerm * 12;

  // const monthlyMortgage = JobDetail?.city_details?.city_details
  //   ?.annual_mortgage_payments
  //   ? Number(
  //       (
  //         JobDetail.city_details.city_details.annual_mortgage_payments / 12
  //       ).toFixed(0),
  //     )
  //   : 2991;
  const defaultMonthlyMortgage = JobDetail?.city_details?.city_details
    ?.annual_mortgage_payments
    ? Number(
        (
          JobDetail.city_details.city_details.annual_mortgage_payments / 12
        ).toFixed(0),
      )
    : 2991;

  const [monthlyMortgage, setMonthlyMortgage] = useState(
    defaultMonthlyMortgage,
  );
  useEffect(() => {
    setMonthlyMortgage(defaultMonthlyMortgage);
  }, [JobDetail]);

  useEffect(() => {
    const calculatedMortgage =
      mortgageAmount > 0
        ? (mortgageAmount *
            monthlyInterest *
            Math.pow(1 + monthlyInterest, totalMonths)) /
          (Math.pow(1 + monthlyInterest, totalMonths) - 1)
        : 0;

    setMonthlyMortgage(calculatedMortgage);
  }, [homePrice, interestRate, loanTerm, downPaymentPercent]);
  const monthlyTaxes = JobDetail?.city_details?.city_details?.annual_taxes_paid
    ? Number(JobDetail.city_details.city_details.annual_taxes_paid / 12)
    : 0;

  // const netMonthlySalary =
  //   JobDetail?.city_details?.city_details
  //     ?.net_monthly_salary_before_mortgage_expense
  //     ? Number(
  //         JobDetail.city_details.city_details
  //           .net_monthly_salary_before_mortgage_expense,
  //       )
  //     : 7801;
  useEffect(() => {
    if (JobDetail?.city_details?.city_details) {
      const cityData = JobDetail.city_details.city_details;

      setHomePrice(Number(cityData.home_prices) || 472900);

      setInterestRate(6.85);

      setDownPaymentPercent(20);

      setLoanTerm(30);

      setNetMonthlySalary(
        Number(cityData?.net_monthly_salary_before_mortgage_expense) || 7801,
      );
    }
  }, [JobDetail]);

  const netPay = React.useMemo(() => {
    // Prefer the accurate per-job estimate (progressive tax on THIS job's
    // salary, from the backend paycheck_estimate). Fall back to the
    // city-median computation for jobs with no posted salary — same
    // "AVG NET PAY IN CITY" behavior as before.
    const est = JobDetail?.paycheck_estimate;
    if (est && Number(est.net_monthly) > 0) return Number(est.net_monthly);
    return computeNetMonthlyIncome(
      JobDetail?.salary,
      JobDetail?.city_details?.city_details?.tax_rate_of_median_salary,
      JobDetail?.city_details?.city_details?.net_annual_salary,
      JobDetail?.city_details?.city_details?.role,
    );
  }, [JobDetail]);

  const grossDisplay = React.useMemo(() => {
    const est = JobDetail?.paycheck_estimate;
    if (est && Number(est.gross_annual) > 0) {
      return {
        annual: Number(est.gross_annual),
        hourly: Number(est.gross_hourly),
      };
    }
    return computeGrossDisplay(
      JobDetail?.salary,
      JobDetail?.city_details?.city_details?.median_annual_salary,
      JobDetail?.city_details?.city_details?.gross_hourly_salary,
      JobDetail?.city_details?.city_details?.role,
    );
  }, [JobDetail]);

  // Renting leftover for the "LEFT OVER EACH MONTH" card — mirrors the
  // For Rent tab formula so both sections always show the same number.
  const rentLeftoverCard = React.useMemo(
    () =>
      netPay -
      Number(
        JobDetail?.city_details?.city_details?.average_monthly_rent_prices || 0,
      ) -
      Number(affordUtilities || 0),
    [netPay, JobDetail, affordUtilities],
  );

  const defaultLeftoverOwning = JobDetail?.city_details?.city_details
    ?.left_over_annually_if_owning
    ? Number(
        (
          JobDetail.city_details.city_details.left_over_annually_if_owning / 12
        ).toFixed(0),
      )
    : 1449;
  const [hasUserChanged, setHasUserChanged] = useState(false);
  const [leftoverOwning, setLeftoverOwning] = useState(defaultLeftoverOwning);
  useEffect(() => {
    setLeftoverOwning(defaultLeftoverOwning);
  }, [JobDetail]);
  useEffect(() => {
    // Always recompute so leftover responds to home-price / utilities edits,
    // not just down-payment slider drags. Formula matches SearchDetail's buy
    // leftover: take-home - mortgage - utilities (netMonthlySalary is already
    // after-tax, so we don't subtract taxes a second time).
    const updatedLeftover =
      netPay - monthlyMortgage - Number(affordUtilities || 0);

    setLeftoverOwning(updatedLeftover);
  }, [monthlyMortgage, netPay, affordUtilities]);

  // ── Live recompute for the donut chart "Where your money goes each month"
  // and its legend, mirroring SearchDetail's logic. Tax stays constant from
  // the API; dollars shift between Mortgage and Left Over so the donut stays
  // proportional to monthly_gross_salary (tax + mortgage + leftover).
  const apiHomePrice = Number(
    JobDetail?.city_details?.city_details?.home_prices || 0,
  );
  const affordCalcLive =
    Number(homePrice) > 0 && Number(homePrice) !== apiHomePrice;

  const monthlyTaxPaid = Number(
    JobDetail?.city_details?.city_details?.monthly_tax_paid || 0,
  );
  const baseMonthlyMortgage = Number(
    JobDetail?.city_details?.city_details?.monthly_mortgage_payment_7_065_apr ||
      0,
  );
  const baseMonthlyLeftOver = Number(
    JobDetail?.city_details?.city_details?.left_over || 0,
  );
  const monthlyGrossSalary =
    monthlyTaxPaid + baseMonthlyMortgage + baseMonthlyLeftOver;

  const liveDonutMortgageValue = affordCalcLive
    ? monthlyMortgage
    : baseMonthlyMortgage;
  const liveDonutLeftOverValue = affordCalcLive
    ? monthlyGrossSalary - monthlyTaxPaid - monthlyMortgage
    : baseMonthlyLeftOver;

  const liveDonutMortgagePct =
    monthlyGrossSalary > 0
      ? (liveDonutMortgageValue / monthlyGrossSalary) * 100
      : 0;
  const liveDonutLeftOverPct =
    monthlyGrossSalary > 0
      ? (liveDonutLeftOverValue / monthlyGrossSalary) * 100
      : 0;

  // Accurate, per-job pie ("Where your money goes each month"): Income Tax
  // (progressive, from paycheck_estimate) + Mortgage (the live-calculated est.
  // mortgage shown in the card) + Left Over (the remainder of monthly gross).
  // Amounts are in dollars so the slices always sum to gross and stay
  // proportional. Null for no-salary jobs, where the existing city-median pie
  // is kept.
  const pieDisplay = React.useMemo(() => {
    const est = JobDetail?.paycheck_estimate;
    if (!est || !(Number(est.gross_annual) > 0)) return null;
    const grossM = Number(est.gross_annual) / 12;
    const taxM = Math.max(0, Number(est.tax_monthly) || 0);
    const mortM = Math.max(0, Number(monthlyMortgage) || 0);
    const leftM = Math.max(0, grossM - taxM - mortM);
    const pct = (n) => (grossM > 0 ? (n / grossM) * 100 : 0);
    return {
      taxAmount: taxM,
      mortgageAmount: mortM,
      leftAmount: leftM,
      taxPct: pct(taxM),
      mortgagePct: pct(mortM),
      leftPct: pct(leftM),
    };
  }, [JobDetail, monthlyMortgage]);

  const liveChartPieData = React.useMemo(() => {
    // Accurate per-job pie when we have a salary estimate.
    if (pieDisplay) {
      return {
        labels: ["Income Tax", "Mortgage", "Left Over"],
        datasets: [
          {
            data: [
              pieDisplay.taxAmount,
              pieDisplay.mortgageAmount,
              pieDisplay.leftAmount,
            ],
            backgroundColor:
              resolvedTheme === "dark"
                ? ["#5B9BC7", "#3FAEE8", "#2A4A5E"]
                : ["#015F88", "#0395D3", "#A8DBF0"],
            borderWidth: 0,
          },
        ],
      };
    }
    // Fallback (no posted salary): existing city-median pie behavior.
    if (
      !affordCalcLive ||
      !chartPieData?.datasets?.length ||
      !Array.isArray(chartPieData.datasets[0]?.data)
    ) {
      return chartPieData;
    }
    // Override all three slices in dollars so Chart.js scales proportions
    // correctly (the original pie_data can mix units across entries).
    const data = [...chartPieData.datasets[0].data];
    if (data.length >= 1) data[0] = Math.max(0, monthlyTaxPaid);
    if (data.length >= 2) data[1] = Math.max(0, liveDonutMortgageValue);
    if (data.length >= 3) data[2] = Math.max(0, liveDonutLeftOverValue);
    return {
      ...chartPieData,
      datasets: [{ ...chartPieData.datasets[0], data }],
    };
  }, [
    pieDisplay,
    chartPieData,
    affordCalcLive,
    monthlyTaxPaid,
    liveDonutMortgageValue,
    liveDonutLeftOverValue,
    resolvedTheme,
  ]);

  const searchOnZillow = async () => {
    if (!address) return;

    const isRent = activePlan === "For Rent";

    const finalUrl = await getZillowLink(address, distance, isRent);

    if (finalUrl) {
      window.open(finalUrl, "_blank");
    }
  };

  const formatCompactPrice = (value) => {
    if (!value) return "0";

    const number = Number(value);

    if (number >= 1000000) {
      return `${(number / 1000000).toFixed(number % 1000000 === 0 ? 0 : 1)}M`;
    }

    if (number >= 1000) {
      return `${(number / 1000).toFixed(number % 1000 === 0 ? 0 : 1)}K`;
    }

    return number.toString();
  };
  const propertyData = {
    "For Sale": {
      cards: [
        {
          key: "mortgage",
          label: "Est. mortgage",
          value: `$${Number(monthlyMortgage.toFixed(0)).toLocaleString(
            "en-US",
          )}`,
          suffix: "/month",
          icon: <CalculatorIcon />,
        },
        {
          key: "utilities",
          label: "Utilities",
          value: `$${Number(affordUtilities || 0).toLocaleString("en-US")}`,
          suffix: "/month",
        },
        {
          key: "leftover",
          label: "Leftover",
          value: `$${Number(leftoverOwning.toFixed(0)).toLocaleString(
            "en-US",
          )}`,
          suffix: "/month",
          icon: <MoneyIcon />,
        },
      ],
      buttons: {
        redfin: {
          label: "View homes for sale on Redfin",
          link: getRedfinZipUrl(JobDetail?.city_details?.city_details?.zip),
        },
        zillow: {
          label: "View homes for sale on Zillow",
        },
      },
    },

    "For Rent": {
      cards: (() => {
        const monthlyRent = Number(
          JobDetail?.city_details?.city_details?.average_monthly_rent_prices ||
            0,
        );
        const rentLeftover =
          netPay - monthlyRent - Number(affordUtilities || 0);
        return [
          {
            key: "rent",
            label: "Median rent",
            value: monthlyRent
              ? `$${Number(monthlyRent).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}`
              : "--",
            suffix: "/month",
            icon: <HomePriceIcon />,
          },
          {
            key: "utilities",
            label: "Utilities",
            value: `$${Number(affordUtilities || 0).toLocaleString("en-US")}`,
            suffix: "/month",
          },
          {
            key: "leftover",
            label: "Leftover",
            value: `$${Number(rentLeftover.toFixed(0)).toLocaleString(
              "en-US",
            )}`,
            suffix: "/month",
            icon: <MoneyIcon />,
          },
        ];
      })(),
      buttons: {
        redfin: {
          label: "View rentals on Redfin",
          link:
            getRedfinZipUrl(JobDetail?.city_details?.city_details?.zip) +
            "/rentals",
        },
        zillow: {
          label: "View rentals on Zillow",
        },
      },
    },
  };
  const currentData = propertyData[activePlan];

  const formatPercent = (val) => {
    if (!val) return 0;
    const num = parseFloat(String(val).replace("%", ""));
    return isNaN(num) ? 0 : num;
  };

  function getGradePercentage(grade) {
    if (!grade) return null; // keep N/A behavior

    const map = {
      "A+": 100,
      A: 95,
      "A-": 90,
      "B+": 85,
      B: 80,
      "B-": 75,
      "C+": 70,
      C: 65,
      "C-": 60,
      "D+": 55,
      D: 50,
      "D-": 45,
      F: 30,
    };

    return map[grade] ?? 10; // 👈 fallback for unknown values
  }
  // function getGradePercentage(grade) {
  //   if (!grade) return null;

  //   const map = {
  //     "A+": 100,
  //     A: 95,
  //     "A-": 90,
  //     "B+": 85,
  //     B: 80,
  //     "B-": 75,
  //     "C+": 70,
  //     C: 65,
  //     "C-": 60,
  //     "D+": 55,
  //     D: 50,
  //     "D-": 45,
  //     F: 30,
  //   };

  //   return map[grade] ?? null; // if unknown → null
  // }
  const grade = JobDetail?.city_details?.city_details?.overall_crime_grade;

  const percent = getGradePercentage(grade);
  const violentGrade =
    JobDetail?.city_details?.city_details?.violent_crime_grade;
  const violentPercent = getGradePercentage(violentGrade);
  const propertyGrade =
    JobDetail?.city_details?.city_details?.property_crime_grade;
  const propertyPercent = getGradePercentage(propertyGrade);
  const otherGrade = JobDetail?.city_details?.city_details?.other_crime_grade;
  const otherPercent = getGradePercentage(otherGrade);

  const savedJobIds = useSelector((state) => state.jobResults.savedJobIds);

  // Server-of-truth bookmark flag for the currently-viewed job, populated
  // by the /frontend/jobs/bookmarks/check/{jobId} API. When this is non-null
  // it takes precedence over the redux `savedJobIds` cache (which can lag
  // behind on a fresh detail view that didn't go through the listing API).
  const [bookmarkChecked, setBookmarkChecked] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !JobDetail?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(checkBookmark(JobDetail.id)).unwrap();
        if (cancelled) return;
        // Accept a few possible response shapes from the backend.
        const payload = res?.data ?? res ?? {};
        const flag =
          payload.bookmarked ??
          payload.is_bookmarked ??
          payload.saved ??
          payload.status ??
          null;
        if (flag !== null && flag !== undefined) {
          setBookmarkChecked(Boolean(Number(flag)));
        }
      } catch (err) {
        // Background check — log only, don't surface a toast.
        console.error("Error checking job bookmark:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, JobDetail?.id, dispatch]);

  //   const isSaved = savedJobIds.includes(JobDetail.id);
  const isSaved =
    bookmarkChecked !== null
      ? bookmarkChecked
      : savedJobIds.includes(JobDetail?.id);
  // if (isLoading) return <p>Loading...</p>;
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!JobDetail) return <p>No job found</p>;

  const jobSeoCompany =
    JobDetail?.company_name || JobDetail?.company?.name || "a trusted employer";
  const jobSeoCity = JobDetail?.location_city || JobDetail?.location?.city || city;
  const jobSeoState = JobDetail?.location_state || JobDetail?.location?.state || state;
  const jobSeoLocation =
    jobSeoCity && jobSeoState ? `${jobSeoCity}, ${jobSeoState}` : "the U.S.";
  const jobSeoDescription = `${JobDetail.title} at ${jobSeoCompany} in ${jobSeoLocation}. See real take-home pay after taxes and housing costs on Map My Pay.`;
  // if (!JobDetail) {
  //   return (
  //     <div className="d-flex justify-content-center align-items-center vh-100 px-3">
  //       <div
  //         className="text-center bg-white p-5 rounded-4"
  //         style={{
  //           maxWidth: "520px",
  //           width: "100%",
  //           boxShadow: "0 10px 35px rgba(1,149,211,0.08)",
  //           border: "1px solid #eef2f7",
  //         }}
  //       >
  //         {/* <div
  //           className="d-flex align-items-center justify-content-center mx-auto mb-4 rounded-circle"
  //           style={{
  //             width: "90px",
  //             height: "90px",
  //             background:
  //               "linear-gradient(135deg, rgba(1,149,211,0.12), rgba(1,95,136,0.08))",
  //             fontSize: "42px",
  //           }}
  //         >
  //           📭
  //         </div> */}

  //         <h2
  //           className="fw-bold mb-3"
  //           style={{
  //             fontSize: "28px",
  //             color: "#0E2A47",
  //           }}
  //         >
  //           Job Not Found
  //         </h2>

  //         <p
  //           className="text-muted mb-4"
  //           style={{
  //             lineHeight: "1.7",
  //             fontSize: "15px",
  //           }}
  //         >
  //           The job you’re looking for may have been removed, expired, or is no
  //           longer available.
  //         </p>

  //         <Button
  //           onClick={() => navigate("/jobs")}
  //           style={{
  //             background: "linear-gradient(135deg, #0195d3, #015f88)",
  //             border: "none",
  //             borderRadius: "12px",
  //             padding: "12px 28px",
  //             fontWeight: "600",
  //           }}
  //         >
  //           Browse Jobs
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }
  return (
    <>
      <div className="job-details-page">
        <div className="app-page-title">
          <div className="page-title-heading d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FiArrowLeft
                size={40}
                className="icon cursor-pointer"
                // Route back to wherever the user entered from. Dashboard
                // sets state.from === "dashboard" on its job-card click;
                // everything else falls back to the Jobs list.
                onClick={() =>
                  from === "dashboard"
                    ? navigate("/dashboard")
                    : navigate("/jobs")
                }
              />
              <h3 className="page-title">Job Detail</h3>
            </div>
            <div
              className="d-flex align-items-center gap-3 page-title-actions-mobile-only"
              style={{ marginRight: "8px" }}
            >
              <Button
                variant="link"
                size="sm"
                onClick={handleShareClick}
                className="p-0 d-flex align-items-center justify-content-center text-muted"
                style={{
                  border: "1px solid var(--mmp-border)",
                  borderRadius: "8px",
                  backgroundColor: "var(--mmp-surface)",
                  width: "40px",
                  height: "40px",
                  minWidth: "40px",
                  transition: "all 0.2s ease",
                  padding: "0 !important",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)";
                  e.currentTarget.style.borderColor = "var(--mmp-border)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--mmp-surface)";
                  e.currentTarget.style.borderColor = "var(--mmp-border)";
                }}
              >
                <Send size={16} />
              </Button>
              <Button
                variant="link"
                size="sm"
                className="p-0 bookmark-btn d-flex align-items-center justify-content-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookmark(JobDetail.id);
                }}
                aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
                style={{
                  border: "1px solid var(--mmp-border)",
                  borderRadius: "8px",
                  backgroundColor: "var(--mmp-surface)",
                  width: "40px",
                  height: "40px",
                  minWidth: "40px",
                  transition: "all 0.2s ease",
                  padding: "0 !important",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)";
                  e.currentTarget.style.borderColor = "var(--mmp-border)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--mmp-surface)";
                  e.currentTarget.style.borderColor = "var(--mmp-border)";
                }}
              >
                {isSaved ? (
                  <BookmarkCheck
                    size={16}
                    style={{ width: 16, height: 16 }}
                    className="text-warning"
                  />
                ) : (
                  <Bookmark
                    size={16}
                    style={{ width: 16, height: 16 }}
                    className="text-secondary"
                  />
                )}
              </Button>
            </div>
          </div>
        </div>
        <div className="job-details-content">
          <SEO
            title={`${JobDetail.title} | Map My Pay Jobs`}
            description={jobSeoDescription}
            url={shareUrl()}
            type="article"
          />
          <div className="job-content">
            <div
              className={`job-card-headers ${
                !hasJobMeta ? "job-card-header--no-metas" : ""
              }`}
            >
              <div className="job-icon">
                <img
                  // src={
                  //   JobDetail?.profile_photo
                  //     ? `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${JobDetail.profile_photo}`
                  //     : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  //       JobDetail?.company_name ||
                  //       JobDetail?.company?.name ||
                  //       "Company",
                  //     )}&background=${avatarBg}&color=fff&size=100&bold=true`
                  // }
                  src={
                    JobDetail?.profile_photo
                      ? JobDetail.profile_photo.startsWith("http")
                        ? JobDetail.profile_photo // ✅ already full URL → use as is
                        : `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${JobDetail.profile_photo}` // ✅ local path
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          JobDetail?.company_name ||
                            JobDetail?.company?.name ||
                            "Company",
                        )}&background=${avatarBg}&color=fff&size=100&bold=true`
                  }
                  alt={
                    JobDetail?.company_name ||
                    JobDetail?.company?.name ||
                    "Company"
                  }
                  className="user-avatar"
                  onError={(e) => {
                    const target = e.target;
                    target.onerror = null;
                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      JobDetail?.company_name ||
                        JobDetail?.company?.name ||
                        "Company",
                    )}&background=${avatarBg}&color=fff&size=100&bold=true`;
                  }}
                />
              </div>

              <div className="job-title-description">
                <div className="title-popular">
                  <div className="d-flex align-items-center justify-content-start gap-2 flex-wrap">
                    {/* Left side: Hospital Name */}
                    <div className="job-title ">
                      {JobDetail?.company_name || JobDetail?.company?.name}
                    </div>

                    <div className="d-flex align-items-center gap-2 badge-container">
                      {JobDetail?.is_featured && (
                        <span className="badge featured-badge">Featured</span>
                      )}
                      {JobDetail?.source === "hospital" &&
                        !JobDetail?.is_featured && (
                          <span
                            className={`badge ${
                              JobDetail?.apply_count > 5
                                ? "hospital-badge hospital-with-popular"
                                : "hospital-badge hospital-alone"
                            }`}
                          >
                            Hospital
                          </span>
                        )}
                      {/* {JobDetail?.apply_count > 5 && (
                        <span className="badge populars">Popular</span>
                      )} */}
                    </div>
                  </div>
                  <span className="job-schedule">
                    <span>Posted {timeAgo(JobDetail?.posted_date)} </span>
                  </span>
                </div>

                {/* <div className="job-description">
                  {JobDetail.promoted_by && (
                    <div className="tag">
                      <div className="tag-desc">
                        Promoted by {JobDetail.promoted_by}
                      </div>
                    </div>
                  )}
                  {JobDetail?.source_name && (
                    <span className="job-desc-text">
                      Responses managed off {JobDetail?.source_name}
                    </span>
                  )}
                </div> */}
              </div>
            </div>

            <div className="content">
              {/* Salary */}
              <div className="job-type">{JobDetail?.title}</div>
              <div className="job-location">
                <MapPin size={14} />
                {(JobDetail?.location_city || JobDetail?.location?.city) &&
                (JobDetail?.location_state || JobDetail?.location?.state)
                  ? `${JobDetail?.location_city ||
                      JobDetail?.location?.city}, ${JobDetail?.location_state ||
                      JobDetail?.location?.state}`
                  : "Location not specified"}
              </div>
              <div className="job-salary">
                {formatSalary(
                  JobDetail?.salary || {
                    min: JobDetail?.salary_min,
                    max: JobDetail?.salary_max,
                    period: JobDetail?.salary_period,
                  },
                )}

                <span className="job-period">/{JobDetail?.salary?.period}</span>
              </div>
              <div className="posted-time">
                {JobDetail?.apply_count} people clicked apply
                {Number(JobDetail?.apply_count) > 5 && " · trending"}
              </div>

              <div className="job-desc">{JobDetail?.description}</div>

              <div className="job-extra-meta">
                <div className="job-meta">
                  <div className="job-meta-left">
                    <div className="job-bonus" data-tour="jd-bonus">
                      <span>Bonus :</span>
                      <strong className="bonus-value">
                        {formatbonus(JobDetail?.bonus)}
                      </strong>
                    </div>

                    <div className="job-bonus">
                      <span>Min Education :</span>
                      <strong>{JobDetail?.min_education}</strong>
                    </div>
                    <div className="job-bonus">
                      <span>Required License :</span>
                      <strong>{JobDetail?.required_license_type}</strong>
                    </div>

                    {/* <div className="job-bonus">
                      <span>Required Certificate :</span>
                      <strong>PALS</strong>
                    </div> */}
                  </div>

                  <div className="job-meta-right">
                    <div className="job-bonus">
                      <span>Min Years Experience :</span>
                      <strong>{JobDetail?.min_years_experience}</strong>
                    </div>
                    <div className="job-bonus">
                      <span>Specialty :</span>
                      <strong className="specialty-value">
                        {JobDetail?.specialty}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="job-type-details-title">Job Type & Details</div>
              <div className="job-schedule tags">
                {JobDetail?.work_model && (
                  <div className="tags">
                    <div className="tag-desc">{JobDetail.work_model}</div>
                  </div>
                )}
                {JobDetail?.shift_type && (
                  <div className="tags">
                    <div className="tag-desc">{JobDetail.shift_type}</div>
                  </div>
                )}

                {JobDetail?.job_type && (
                  <div className="tags">
                    <div className="tag-desc">{JobDetail.job_type}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="buttons pt-0">
              {/* Apply Button */}

              <Button
                className="w-100 btn btn-secondary"
                data-tour="jd-apply"
                onClick={async () => {
                  if (!JobDetail?.id) return;

                  try {
                    const session_id = sessionStorage.getItem(
                      "comparison_session_id",
                    );
                    if (session_id) {
                      // 🔥 Track application click
                      dispatch(
                        jobTracking({
                          session_id,
                          event_type: "application_clicked",
                          job_id: JobDetail.id,
                          job_title: JobDetail?.title || null,
                          specialty: selectedRole || null,
                          job_type: selectedJobType?.[0] || null,
                          shift_type: selectedShiftType?.[0] || null,
                          company_name:
                            JobDetail?.company_name ||
                            JobDetail?.company?.name ||
                            null,
                          timestamp: new Date().toISOString(),
                        }),
                      );
                    }

                    // 🔥 Call apply API
                    // await dispatch(applyJob(JobDetail.id)).unwrap();

                    // 🔥 Open application URL
                    const url = getApplyUrl(JobDetail?.application_url);
                    if (url && url !== "#") {
                      // Increment the visible "people clicked apply" counter
                      // (authenticated-only endpoint — skip for guests).
                      if (isAuthenticated) {
                        jobsService
                          .trackApplyClick(JobDetail.id)
                          .catch(() => {});
                      }
                      window.open(url, "_blank", "noopener,noreferrer");
                    }
                  } catch (error) {
                    console.error("Apply failed:", error);
                  }
                }}
              >
                Apply Now
              </Button>
              {/* <Button
              className="w-100 btn btn-secondary"
              onClick={async (e) => {
                e.stopPropagation();
                if (!JobDetail?.id) return;

                const session_id = sessionStorage.getItem(
                  "comparison_session_id",
                );
                if (!session_id) return;

                try {
                  // Track application click
                  dispatch(
                    jobTracking({
                      session_id,
                      event_type: "application_clicked",
                      job_id: JobDetail.id,
                      job_title: JobDetail?.title || null,
                      specialty: selectedRole || null,
                      job_type: selectedJobType?.[0] || null,
                      shift_type: selectedShiftType?.[0] || null,
                      company_name:
                        JobDetail?.company_name ||
                        JobDetail?.company?.name ||
                        null,
                      timestamp: new Date().toISOString(),
                    }),
                  );

                  // ⭐ If job source is hospital → check Redux state
                  if (JobDetail?.source === "hospital") {
                    // If has_applied is true, treat like external job (open URL)
                    if (hasApplied === true) {
                      const url = getApplyUrl(JobDetail?.application_url);
                      if (url && url !== "#") {
                        window.open(url, "_blank", "noopener,noreferrer");
                      }
                    } else {
                      // If has_applied is false, navigate to apply job page
                      navigate(`/jobs/apply/${JobDetail.id}`);
                    }
                    return;
                  }

                  // ⭐ Otherwise open external link
                  const url = getApplyUrl(JobDetail?.application_url);
                  if (url && url !== "#") {
                    window.open(url, "_blank", "noopener,noreferrer");
                  }
                } catch (error) {
                  console.error("Apply failed:", error);
                }
              }}
            >
              Apply Now
            </Button> */}

              {shareModal && (
                <div
                  className="modal-backdrop-overlay"
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    zIndex: 99998,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={closeShareModal}
                >
                  <div
                    className="modal-content-wrapper"
                    style={{
                      backgroundColor: "var(--mmp-surface)",
                      borderRadius: "12px",
                      maxWidth: "400px",
                      width: "90%",
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                      zIndex: 99999,
                      position: "relative",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Modal Header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px",
                        borderBottom: "1px solid var(--mmp-border)",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "var(--mmp-text-primary)",
                          margin: 0,
                          lineHeight: "24px",
                        }}
                      >
                        Share Job
                      </h3>
                      <button
                        onClick={closeShareModal}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          borderRadius: "4px",
                          color: "var(--mmp-text-secondary)",
                          fontSize: "24px",
                          lineHeight: 1,
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div style={{ padding: "0" }}>
                      {/* Twitter */}
                      <div
                        onClick={() => shareToSocialMedia("twitter")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--mmp-border)",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            backgroundColor: "#1da1f2",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <FaXTwitter
                            size={14}
                            style={{
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        </div>

                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          Share on Twitter
                        </span>
                      </div>

                      {/* Facebook */}
                      <div
                        onClick={() => shareToSocialMedia("facebook")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--mmp-border)",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            backgroundColor: "#1877f2",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "10px",
                            fontWeight: "bold",
                          }}
                        >
                          <FaFacebookF
                            size={14}
                            style={{
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        </div>
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          Share on Facebook
                        </span>
                      </div>

                      {/* WhatsApp */}
                      <div
                        onClick={() => shareToSocialMedia("whatsapp")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--mmp-border)",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            backgroundColor: "#25d366",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          <FaWhatsapp
                            size={14}
                            style={{
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        </div>
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          Share on WhatsApp
                        </span>
                      </div>

                      {/* LinkedIn */}
                      <div
                        onClick={() => shareToSocialMedia("linkedin")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--mmp-border)",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            backgroundColor: "#0077b5",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "10px",
                            fontWeight: "bold",
                          }}
                        >
                          <FaLinkedinIn
                            size={14}
                            style={{
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        </div>
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          Share on LinkedIn
                        </span>
                      </div>

                      {/* Copy Link */}
                      <div
                        onClick={() => shareToSocialMedia("copy")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          borderBottomLeftRadius: "12px",
                          borderBottomRightRadius: "12px",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-hover-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <Copy
                          style={{
                            width: "20px",
                            height: "20px",
                            color: "var(--mmp-text-secondary)",
                          }}
                        />
                        <span style={{ fontSize: "14px", fontWeight: "500" }}>
                          Copy Link
                        </span>
                      </div>

                      {/* Share to Community */}
                      <div
                        onClick={() => {
                          closeShareModal();
                          const jobContent = generateJobPostContent();
                          setPreFilledContent(jobContent);
                          setShowCreatePostModal(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 24px",
                          cursor: "pointer",
                          borderTop: "1px solid var(--mmp-border)",
                          transition: "background-color 0.2s ease",
                          borderBottomLeftRadius: "12px",
                          borderBottomRightRadius: "12px",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "var(--mmp-accent-bg)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <Users
                          style={{
                            width: "20px",
                            height: "20px",
                            color: "var(--mmp-accent)",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "var(--mmp-accent)",
                          }}
                        >
                          Share to Community
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Post Modal for sharing to community */}
              {/* <CreatePostModal
                show={showCreatePostModal}
                onHide={() => {
                  setShowCreatePostModal(false);
                  setPreFilledContent("");
                }}
                createPost={createPost}
                categories={categories}
                isLoading={categoriesLoading}
                initialCategory={jobListingCategory?.category_name || "Job Listing"}
                preFilledContent={preFilledContent}
                onpostSubmit={() => {}}
                onCategoryChange={() => {}}
              /> */}
              {showCreatePostModal && (
                <CreatePostModal
                  show={true}
                  onHide={() => {
                    setShowCreatePostModal(false);
                    setPreFilledContent("");
                  }}
                  createPost={createPost}
                  categories={categories}
                  isLoading={categoriesLoading}
                  initialCategory={
                    jobListingCategory?.category_name || "Job Listing"
                  }
                  preFilledContent={preFilledContent}
                  onpostSubmit={() => {}}
                  onCategoryChange={() => {}}
                />
              )}
            </div>
            {jobViewUsage &&
              jobViewUsage.limit >= 0 &&
              (jobViewUsage.plan_slug === "free" ||
                jobViewUsage.plan_slug === "free-trial") && (
                <div className="plan-banner">
                  <div className="plan-banner-text">
                    Free plan: {jobViewUsage.used} of {jobViewUsage.limit} job
                    detail{jobViewUsage.limit === 1 ? "" : "s"} viewed today
                  </div>
                  <button
                    type="button"
                    className="upgrade-btn"
                    onClick={() =>
                      navigate("/profile/subscription", {
                        state: { source: "job_view_banner" },
                      })
                    }
                  >
                    Upgrade
                  </button>
                </div>
              )}
          </div>
        </div>
        <div className="city-header-new-design mt-3" data-tour="jd-snapshot">
          <div className="header-text-info">
            <div className="role-label">
              {JobDetail?.city_details?.city_details?.role?.toUpperCase()}
            </div>
            <h1 className="city-state-title">
              {JobDetail?.city_details?.city_details?.city},{" "}
              {JobDetail?.city_details?.city_details?.state}
            </h1>
          </div>
          <div className="metrics-cards-container">
            {(() => {
              const appDisplay = String(
                JobDetail?.city_details?.city_details?.app_display || "",
              )
                .trim()
                .toLowerCase();
              let crimeVariant = "is-default";
              if (appDisplay === "moderate") crimeVariant = "is-moderate";
              else if (appDisplay === "mild") crimeVariant = "is-mild";
              else if (appDisplay === "severe") crimeVariant = "is-severe";
              return (
                <div
                  className={`metric-card crime-card-new ${crimeVariant}`}
                  data-tour="jd-crime"
                >
                  <div className="icon-wrapper">
                    <CrimeIcon />
                  </div>
                  <div className="metric-content">
                    <div className="metric-label">Crime</div>
                    <div className="metric-value">
                      <span className="crime-level">
                        {JobDetail?.city_details?.city_details?.app_display}
                      </span>
                      <span className="separator"> · </span>
                      <span className="crime-grade">
                        {
                          JobDetail?.city_details?.city_details
                            ?.overall_crime_grade
                        }
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div
              className="metric-card hospital-card-new"
              data-tour="jd-hosp"
              onClick={() => setIsHospitalsExpanded(true)}
              style={{ cursor: "pointer" }}
            >
              <div className="icon-wrapper">
                <HospitalsIcon />
              </div>
              <div className="metric-content">
                <div className="metric-label">Hospitals</div>
                <div className="metric-value">
                  <span className="hospital-count">
                    {JobDetail?.city_details?.city_details?.hospital_count}
                  </span>
                  <span className="nearby-text"> nearby</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <aside className="job-detail-jobs-rail">
          <aside className="quick-actions-panel" aria-label="Quick actions">
            <div className="quick-actions-title">QUICK ACTIONS</div>
            <button
              type="button"
              className="quick-action-row"
              onClick={() => {
                if (!isAuthenticated) {
                  requireAuth("Log in or sign up to view this job on the map.");
                  return;
                }
                navigate("/full-map", {
                  state: {
                    fromCityDetail: {
                      city: JobDetail?.city_details?.city_details?.city || "",
                      state: JobDetail?.city_details?.city_details?.state || "",
                      role: JobDetail?.city_details?.city_details?.role || "",
                    },
                  },
                });
              }}
            >
              <span className="qa-icon qa-icon-map">
                <FiMapPin />
              </span>
              <span className="qa-label">View on map</span>
            </button>
            <button
              type="button"
              className={`quick-action-row ${isSaved ? "is-saved" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleBookmark(JobDetail.id);
              }}
              aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
            >
              <span className="qa-icon">
                {isSaved ? (
                  <BookmarkCheck style={{ width: 18, height: 18 }} />
                ) : (
                  <Bookmark style={{ width: 18, height: 18 }} />
                )}
              </span>
              <span className="qa-label">{isSaved ? "Saved" : "Save"}</span>
            </button>
            <button
              type="button"
              className="quick-action-row"
              onClick={handleShareClick}
            >
              <span className="qa-icon">
                <Send size={18} />
              </span>
              <span className="qa-label">Share</span>
            </button>
          </aside>
          {
            <div className="hospital-sidebar-card" data-tour="jd-hospitals">
              <div
                className="job-card-title"
                onClick={() =>
                  otherHospitals.length > 0 &&
                  setIsHospitalsExpanded(!isHospitalsExpanded)
                }
                style={
                  otherHospitals.length === 0
                    ? { cursor: "default" }
                    : undefined
                }
              >
                <span>
                  {hospitalCountLabel}
                  <InfoTooltip text="Hospitals in this city, closest first. Tap to expand, then click a hospital to open its own careers website in a new tab." />
                </span>
                {otherHospitals.length > 0 &&
                  (isHospitalsExpanded ? (
                    <FiChevronUp
                      size={20}
                      style={{ transition: "transform 0.3s ease" }}
                    />
                  ) : (
                    <FiChevronDown
                      size={20}
                      style={{ transition: "transform 0.3s ease" }}
                    />
                  ))}
              </div>
              <div className="hospital-body">
                {!primaryHospital && (
                  <p className="no-hospitals">No Hospitals available</p>
                )}
                {primaryHospital && (
                  <div className="hospitals-container nearby-list">
                    <div
                      className="hospital-item"
                      data-tour="jd-hospital-item"
                      onClick={() =>
                        handleHospitalCareersClick(primaryHospital?.careers_url)
                      }
                      style={{
                        cursor: primaryHospital?.careers_url
                          ? "pointer"
                          : "default",
                      }}
                    >
                      <div className="content-wrap">
                        <div className="hospital-icon">
                          <HospitalIcon />
                        </div>
                        <div className="hospital-details">
                          <div className="hospital-name">
                            {primaryHospital?.hospital_name}
                          </div>
                          <div className="hospital-address">
                            {primaryHospital?.full_address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {otherHospitals.length > 0 && (
                  <Collapse in={isHospitalsExpanded}>
                    <div>
                      <div className="hospitals-container nearby-list">
                        {otherHospitals.map((hospital, index) => (
                          <div
                            key={index}
                            className="hospital-item"
                            onClick={() =>
                              handleHospitalCareersClick(hospital?.careers_url)
                            }
                            style={{
                              cursor: hospital?.careers_url
                                ? "pointer"
                                : "default",
                            }}
                          >
                            <div className="content-wrap">
                              <div className="hospital-icon">
                                <HospitalIcon />
                              </div>
                              <div className="hospital-details">
                                <div className="hospital-name">
                                  {hospital?.hospital_name}
                                </div>
                                <div className="hospital-address">
                                  {hospital?.full_address}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Collapse>
                )}
              </div>
            </div>
          }
          <Card
            className="city-all-contents mt-2 mb-2 pb-0"
            data-tour="jd-jobs"
          >
            <Card.Body className="p-0">
              <div className="job-card-collapsed">
                <div
                  className="job-card-title"
                  onClick={() => setIsJobsExpanded(!isJobsExpanded)}
                >
                  <span
                    style={{
                      color: "var(--mmp-text-primary)",
                      fontWeight: 700,
                      fontSize: "15px",
                    }}
                  >
                    Jobs for this city{" "}
                    <span
                      style={{
                        color: "var(--mmp-text-secondary)",
                        fontWeight: 700,
                        fontSize: "15px",
                      }}
                    >
                      {jobs?.length > 0 && `(${jobs.length})`}
                    </span>
                    <InfoTooltip text="Browse other jobs available in this city. Expand a listing to see a quick overview or select Apply to view the full job details." />
                  </span>
                  {isJobsExpanded ? (
                    <FiChevronUp
                      size={20}
                      style={{ transition: "transform 0.3s ease" }}
                    />
                  ) : (
                    <FiChevronDown
                      size={20}
                      style={{ transition: "transform 0.3s ease" }}
                    />
                  )}
                </div>
                <Collapse in={isJobsExpanded}>
                  <div>
                    {jobsLoading ? (
                      <div
                        className="jobs-loading-container"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "40px 20px",
                          width: "100%",
                        }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p
                          style={{
                            marginTop: "12px",
                            color: "var(--mmp-text-secondary)",
                            fontSize: "14px",
                          }}
                        >
                          Loading jobs...
                        </p>
                      </div>
                    ) : jobs?.length === 0 ? (
                      <p className="no-jobs">No Jobs available</p>
                    ) : (
                      <Row className="g-2 p-2">
                        {jobs.map((job) => (
                          <Col
                            key={job.id}
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            className="search-job-card"
                          >
                            <Jobcards
                              job={job}
                              onApply={(jobId) => {
                                if (!isAuthenticated) {
                                  requireAuth(
                                    "Log in or sign up to view this job's details.",
                                  );
                                  return;
                                }
                                if (jobViewLimitDataRef.current) {
                                  setJobViewLimitInfo(
                                    jobViewLimitDataRef.current,
                                  );
                                  return;
                                }
                                navigate(`/job-detail/${jobId}`);
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                </Collapse>
              </div>
            </Card.Body>
          </Card>
          <div
            className="city-more-card city-more-card-rail-only"
            data-tour="jd-citycta"
          >
            <div className="city-more-title">
              More about {JobDetail?.city_details?.city_details?.city},{" "}
              {JobDetail?.city_details?.city_details?.state}
              <InfoTooltip text="Click to explore detailed information about this city, including demographics, schools, climate, licensing requirements, and more." />
            </div>

            <div className="city-more-description">
              Demographics, schools, climate, license requirements, and more
            </div>

            <button className="city-more-btn" onClick={handleCityDetailClick}>
              View {JobDetail?.city_details?.city_details?.city} city details
              <span>→</span>
            </button>
          </div>
          <div
            className="similar-jobs-section similar-jobs-rail-only"
            data-tour="jd-similar"
          >
            <div className="similar-title">
              Similar jobs
              <InfoTooltip text="Explore similar jobs matching this role and location. Click any job to view its full details and apply." />
            </div>

            <div className="similar-subtitle">
              Other openings matched by role and city
            </div>

            {similarJobsLoading && similarPage === 1 ? (
              <div className="similar-jobs-loading">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : !similarJobs?.data?.length ? (
              <p className="no-jobs">No similar jobs found</p>
            ) : (
              <>
                <div className="similar-jobs-grid">
                  {similarJobs.data.map((job, i) => (
                    <div
                      className="similar-job-card"
                      data-tour={i === 0 ? "jd-similar0" : undefined}
                      key={job.id}
                      onClick={() => {
                        if (!job?.id) return;
                        if (!isAuthenticated) {
                          requireAuth(
                            "Log in or sign up to view this job's details.",
                          );
                          return;
                        }
                        if (jobViewLimitDataRef.current) {
                          setJobViewLimitInfo(jobViewLimitDataRef.current);
                          return;
                        }
                        navigate(`/job-detail/${job.id}`);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {/* LEFT */}
                      <div className="job-left">
                        <div className="job-logo">
                          <img
                            src={getSimilarAvatarSrc(job)}
                            alt={getSimilarCompany(job)}
                            className="user-avatar"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                getSimilarCompany(job),
                              )}&background=${getSimilarAvatarBg(
                                job,
                              )}&color=fff&size=100&bold=true`;
                            }}
                          />
                        </div>

                        <div className="job-content-card">
                          <div className="job-title">{job.title}</div>

                          <div className="job-hospital">
                            {getSimilarCompany(job)}
                          </div>

                          <div className="job-salary">
                            {formatSimilarSalary(job)}
                          </div>
                        </div>
                      </div>

                      {/* RIGHT */}
                      <div className="job-right">
                        <div className="leftover-price">
                          {formatSimilarLeftover(job)}
                        </div>

                        {hasSimilarSalary(job) && (
                          <div className="leftover-label">
                            AFTER HOUSING /MO
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {((similarJobs?.page || 1) < (similarJobs?.pages || 1) ||
                    (similarJobs?.data?.length || 0) <
                      (similarJobs?.total || 0)) && (
                    <div className="similar-show-more-wrap">
                      <Button
                        className="similar-show-more-btn"
                        onClick={handleLoadMoreSimilar}
                        disabled={similarJobsLoading}
                      >
                        {similarJobsLoading ? "Loading…" : "Show more"}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="live-work-card">
            <div className="live-work-header">
              <span className="live-work-icon">
                <FiMapPin />
              </span>
              <span className="live-work-title">Live Here, Work There</span>
            </div>
            <p className="live-work-subtitle">
              Compare{" "}
              {JobDetail?.city_details?.city_details?.city || "this city"}{" "}
              against another city for relocation. Pre-filled as your work city.
            </p>
            <div className="live-work-field">
              <label>WORK</label>
              <div className="live-work-input is-readonly">
                {JobDetail?.city_details?.city_details?.city || ""}
                {JobDetail?.city_details?.city_details?.state
                  ? `, ${JobDetail.city_details.city_details.state}`
                  : ""}
              </div>
            </div>
            <div className="live-work-field">
              <label>LIVE</label>
              <button
                ref={liveLocTriggerRef}
                type="button"
                className={`live-work-input live-work-loc-trigger ${
                  liveSelectedCity || liveSelectedState ? "" : "is-placeholder"
                }`}
                onClick={() => setLiveLocOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={liveLocOpen}
              >
                <span>
                  {liveSelectedCity?.city
                    ? `${liveSelectedCity.city}${
                        liveSelectedState?.state
                          ? `, ${liveSelectedState.state}`
                          : ""
                      }`
                    : liveSelectedState
                    ? liveSelectedState.state_full_name ||
                      liveSelectedState.state
                    : "Choose a city"}
                </span>
                <FiChevronDown
                  style={{
                    transform: liveLocOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.15s ease",
                  }}
                />
              </button>
            </div>
            <button
              type="button"
              className="live-work-btn"
              onClick={() => {
                if (!isAuthenticated) {
                  requireAuth(
                    "Log in or sign up to check Live Here, Work There.",
                  );
                  return;
                }
                const workStateCode =
                  JobDetail?.city_details?.city_details?.state || "";
                const workStateMatch = (liveAllStates || []).find(
                  (s) => s.state === workStateCode,
                );
                const workStateFullName =
                  workStateMatch?.state_full_name || workStateCode;
                const roleName =
                  JobDetail?.city_details?.city_details?.role || "";

                navigate("/comparison", {
                  state: {
                    NursingRole: roleName
                      ? { value: roleName, label: roleName }
                      : undefined,
                    WorkState: workStateCode
                      ? {
                          value: workStateCode,
                          label: workStateFullName,
                        }
                      : undefined,
                    WorkCity: JobDetail?.city_details?.city_details?.city
                      ? {
                          value: JobDetail.city_details.city_details.city,
                          label: JobDetail.city_details.city_details.city,
                        }
                      : undefined,
                    LiveState: liveSelectedState
                      ? {
                          value: liveSelectedState.state,
                          label:
                            liveSelectedState.state_full_name ||
                            liveSelectedState.state,
                        }
                      : undefined,
                    LiveCity: liveSelectedCity?.city
                      ? {
                          value: liveSelectedCity.city,
                          label: liveSelectedCity.city,
                        }
                      : undefined,
                  },
                });
              }}
            >
              Check Live Here, Work There
            </button>

            {liveLocOpen &&
              liveLocPos &&
              createPortal(
                <div
                  ref={liveLocPopoverRef}
                  className="live-work-loc-popover"
                  style={{
                    position: "fixed",
                    top: liveLocPos.top,
                    left: liveLocPos.left,
                    width: liveLocPos.width,
                    maxHeight: liveLocPos.maxHeight || 320,
                    zIndex: 9500,
                  }}
                  role="listbox"
                >
                  {(liveSelectedState || liveSelectedCity) && (
                    <button
                      type="button"
                      className="live-loc-clear"
                      onClick={() => {
                        setLiveSelectedState(null);
                        setLiveSelectedCity(null);
                        setLiveLocOpen(false);
                      }}
                    >
                      Clear all
                    </button>
                  )}
                  {!liveSelectedState && (
                    <>
                      <input
                        type="text"
                        className="live-loc-search"
                        value={liveStateSearch}
                        onChange={(e) => setLiveStateSearch(e.target.value)}
                        placeholder="Search state…"
                        autoFocus
                      />
                      {(!liveAllStates || liveAllStates.length === 0) && (
                        <div className="live-loc-option is-empty">Loading…</div>
                      )}
                      {liveAllStates &&
                        liveAllStates.length > 0 &&
                        (() => {
                          const q = liveStateSearch.trim().toLowerCase();
                          const filtered = liveAllStates.filter((s) => {
                            if (!q) return true;
                            return (
                              (s.state_full_name || "")
                                .toLowerCase()
                                .includes(q) ||
                              (s.state || "").toLowerCase().includes(q)
                            );
                          });
                          if (filtered.length === 0) {
                            return (
                              <div className="live-loc-option is-empty">
                                No states found
                              </div>
                            );
                          }
                          return filtered.map((s) => (
                            <button
                              key={s.state}
                              type="button"
                              className="live-loc-option"
                              onClick={() => {
                                setLiveSelectedState(s);
                                setLiveSelectedCity(null);
                              }}
                              role="option"
                            >
                              {s.state_full_name || s.state}
                            </button>
                          ));
                        })()}
                    </>
                  )}

                  {liveSelectedState && (
                    <>
                      <div className="live-loc-header">
                        <span>
                          {liveSelectedState.state_full_name ||
                            liveSelectedState.state}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setLiveSelectedState(null);
                            setLiveSelectedCity(null);
                          }}
                        >
                          Change
                        </button>
                      </div>
                      <input
                        type="text"
                        className="live-loc-search"
                        value={liveCitySearch}
                        onChange={(e) => setLiveCitySearch(e.target.value)}
                        placeholder="Search city…"
                        autoFocus
                      />
                      {liveCitiesLoading && (
                        <div className="live-loc-option is-empty">Loading…</div>
                      )}
                      {!liveCitiesLoading &&
                        (() => {
                          const q = liveCitySearch.trim().toLowerCase();
                          const filtered = (liveAllCities || []).filter((c) => {
                            if (!q) return true;
                            return (c.city || "").toLowerCase().includes(q);
                          });
                          if (filtered.length === 0) {
                            return (
                              <div className="live-loc-option is-empty">
                                No cities found
                              </div>
                            );
                          }
                          return filtered.map((c, i) => {
                            const isSel =
                              liveSelectedCity &&
                              liveSelectedCity.city === c.city;
                            return (
                              <button
                                key={`${c.city}-${i}`}
                                type="button"
                                className={`live-loc-option ${
                                  isSel ? "is-selected" : ""
                                }`}
                                onClick={() => {
                                  setLiveSelectedCity(c);
                                  setLiveLocOpen(false);
                                }}
                                role="option"
                                aria-selected={isSel}
                              >
                                {c.city}
                              </button>
                            );
                          });
                        })()}
                    </>
                  )}
                </div>,
                document.body,
              )}
          </div>
        </aside>
        <div className="search-details-content mt-3">
          <div className="salary-housing-section" data-tour="jd-salary">
            <div className="salary-housing-top">
              <div className="salary-housing-title">
                Salary & Housing — city average
              </div>
              <div className="salary-housing-subtitle">
                Based on all {JobDetail?.city_details?.city_details?.role} roles
                in {JobDetail?.city_details?.city_details?.state}.
              </div>
            </div>

            <div className="salary-home-grid">
              {/* Gross Salary Card */}
              <div className="salary-home-card">
                <div className="card-label">
                  This Job's salary in{" "}
                  {JobDetail?.city_details?.city_details?.city},{" "}
                  {JobDetail?.city_details?.city_details?.state}
                  <InfoTooltip text="The average of this job's posted minimum and maximum salary, shown as a yearly and hourly rate." />
                </div>

                <div className="card-price">
                  ${formatCompactPrice(grossDisplay.annual)}/yr
                </div>

                <div className="card-subtext">
                  $
                  {Number(
                    Number(grossDisplay.hourly).toFixed(2),
                  ).toLocaleString("en-US")}
                  /hr
                </div>
              </div>

              {/* Home Price Card */}
              <div className="salary-home-card">
                <div className="card-label">
                  Typical Home Value for the Region
                  <InfoTooltip text="The median price of a single-family home in this city." />
                </div>

                <div className="card-price">
                  $
                  {formatCompactPrice(
                    JobDetail?.city_details?.city_details?.home_prices,
                  )}
                </div>

                <div className="card-subtext">single-family</div>
              </div>
            </div>
            <div className="leftover-balance-card" data-tour="jd-leftover">
              <div className="leftovers-title">LEFT OVER EACH MONTH</div>

              <div className="leftover-grid">
                {/* Owning */}
                <div className="leftover-item">
                  <div className="leftover-labels">
                    Owning
                    <InfoTooltip text="Your salary left over each month after deducting the mortgage and other monthly expenses." />
                  </div>

                  <div
                    className={`leftover-price ${
                      leftoverOwning < 0 ? "negative" : ""
                    }`}
                  >
                    $
                    {Number(Math.round(leftoverOwning || 0)).toLocaleString(
                      "en-US",
                    )}
                  </div>
                </div>

                {/* Renting */}
                <div className="leftover-item border-left">
                  <div className="leftover-labels">
                    Renting
                    <InfoTooltip text="Your salary left over each month after deducting rent and other monthly expenses." />
                  </div>

                  <div
                    className={`leftover-price ${
                      rentLeftoverCard < 0 ? "negative" : ""
                    }`}
                  >
                    $
                    {Number(Math.round(rentLeftoverCard || 0)).toLocaleString(
                      "en-US",
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="money-month">
              <div className="money-month-title">
                Where your money goes each month
                <InfoTooltip text="See how your estimated monthly pay is divided between income taxes, housing costs, and your remaining take-home pay." />
              </div>

              <div className="money-month-wrapper">
                {/* LEFT GRAPH */}
                <div className="monthly-graph">
                  <div className="chart-wrap">
                    <Doughnut
                      data={liveChartPieData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "50%",
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            backgroundColor: "rgba(0,0,0,0.85)",
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* RIGHT LEGEND */}
                <div className="money-month-legend">
                  {/* Income Tax */}
                  <div className="item">
                    <div className="left">
                      <span className="key taxes"></span>

                      <div className="text">
                        <div className="label">Income Tax</div>

                        <div className="percentage">
                          {(pieDisplay
                            ? pieDisplay.taxPct
                            : formatPercent(
                                JobDetail?.city_details?.pie_data?.[0]?.text,
                              )
                          ).toFixed(0)}
                          %
                        </div>
                      </div>
                    </div>

                    <div className="price">
                      $
                      {pieDisplay
                        ? Number(
                            pieDisplay.taxAmount.toFixed(0),
                          ).toLocaleString("en-US")
                        : JobDetail?.city_details?.city_details
                            ?.monthly_tax_paid
                        ? Number(
                            Number(
                              JobDetail.city_details.city_details
                                .monthly_tax_paid,
                            ).toFixed(0),
                          ).toLocaleString("en-US")
                        : "0"}
                    </div>
                  </div>

                  {/* Mortgage */}
                  <div className="item">
                    <div className="left">
                      <span className="key mortgage"></span>

                      <div className="text">
                        <div className="label">Mortgage</div>

                        <div className="percentage">
                          {(pieDisplay
                            ? pieDisplay.mortgagePct
                            : affordCalcLive
                            ? liveDonutMortgagePct
                            : formatPercent(
                                JobDetail?.city_details?.pie_data?.[1]?.text,
                              )
                          ).toFixed(0)}
                          %
                        </div>
                      </div>
                    </div>

                    <div className="price">
                      $
                      {Number(
                        Math.round(
                          (pieDisplay
                            ? pieDisplay.mortgageAmount
                            : liveDonutMortgageValue) || 0,
                        ),
                      ).toLocaleString("en-US")}
                    </div>
                  </div>

                  {/* Left Over */}
                  <div className="item">
                    <div className="left">
                      <span className="key leftover"></span>

                      <div className="text">
                        <div className="label">Left Over</div>

                        <div className="percentage">
                          {(pieDisplay
                            ? pieDisplay.leftPct
                            : affordCalcLive
                            ? liveDonutLeftOverPct
                            : Number(
                                JobDetail?.city_details?.city_details
                                  ?.left_over_percent ?? 0,
                              )
                          ).toFixed(0)}
                          %
                        </div>
                      </div>
                    </div>

                    <div className="price">
                      $
                      {Number(
                        Math.round(
                          (pieDisplay
                            ? pieDisplay.leftAmount
                            : leftoverOwning) || 0,
                        ),
                      ).toLocaleString("en-US")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="salary-afford-card" data-tour="jd-afford">
            {/* HEADER */}
            <div className="salary-afford-header">
              <JobCalculator className="button-icon" />

              <div className="salary-afford-title">
                Can you afford to live here on this salary?
              </div>

              <InfoTooltip text="Compares this job's estimated monthly take-home pay with local rent and mortgage costs to help you evaluate affordability." />
            </div>
            <div className="salary-afford-subtitle">
              {(() => {
                const rawMin = Number(JobDetail?.salary?.min || 0);
                const rawMax = Number(JobDetail?.salary?.max || 0);
                const period = JobDetail?.salary?.period?.toLowerCase();
                const salaryText =
                  (rawMin > 0 || rawMax > 0) && period
                    ? `$${rawMin.toLocaleString(
                        "en-US",
                      )} - $${rawMax.toLocaleString("en-US")}/${period}`
                    : "";
                const role = JobDetail?.city_details?.city_details?.role || "";
                const isRN = role.toLowerCase().includes("registered nurse");
                const roleHours = isRN ? 1872 : 2080;
                const hoursText = `${roleHours} hrs/yr`;
                return salaryText
                  ? `Based on this job's posted ${salaryText} · ${hoursText}`
                  : `Based on ${hoursText}`;
              })()}
            </div>

            {/* CALCULATIONS */}
            {(() => {
              // Rent comes from the city's median rent (API), not derived
              // from mortgage. The previous formula (monthlyMortgage * 0.66)
              // made the Renting % progress bar move whenever the user
              // changed the down-payment slider, which was misleading.
              const rentAmount = Number(
                JobDetail?.city_details?.city_details
                  ?.average_monthly_rent_prices || 0,
              );

              const rentPercent = Math.round((rentAmount * 100) / netPay);

              const mortgagePercent = Math.round(
                (monthlyMortgage * 100) / netPay,
              );

              return (
                <>
                  {/* TOP CARDS */}
                  <div className="salary-afford-grid">
                    {/* NET PAY */}
                    <div className="salary-box">
                      <div className="box-label">
                        {(() => {
                          const min = Number(JobDetail?.salary?.min || 0);
                          const max = Number(JobDetail?.salary?.max || 0);
                          const period = JobDetail?.salary?.period?.toLowerCase();
                          const avg = min > 0 || max > 0 ? (min + max) / 2 : 0;
                          const hasRate =
                            avg > 0 &&
                            [
                              "hourly",
                              "weekly",
                              "monthly",
                              "yearly",
                              "annual",
                            ].includes(period);
                          if (hasRate) return "THIS JOB'S NET PAY";
                          const role =
                            JobDetail?.city_details?.city_details?.role || "";
                          const city =
                            JobDetail?.city_details?.city_details?.city || "";
                          return `AVG NET PAY${role ? ` · ${role}` : ""}${
                            city ? ` IN ${city.toUpperCase()}` : ""
                          }`;
                        })()}
                        <InfoTooltip text="Estimated monthly take-home pay after taxes. If this job doesn't include a salary, we use the average pay for this role in the selected city." />
                      </div>

                      <div className="box-price">
                        ${Number(netPay.toFixed(0)).toLocaleString("en-US")}
                      </div>

                      <div className="box-subtext">/month after tax</div>
                    </div>

                    {/* RENT */}
                    <div className="salary-box">
                      <div className="box-label">
                        RENT
                        <InfoTooltip text="Estimated median monthly rent for this city, used to compare housing costs with your take-home pay." />
                      </div>

                      <div className="box-price">
                        {JobDetail?.city_details?.city_details
                          ?.average_monthly_rent_prices
                          ? `$${Number(
                              JobDetail.city_details.city_details
                                .average_monthly_rent_prices,
                            ).toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })}`
                          : "--"}
                      </div>

                      <div className="box-subtext">city median</div>
                    </div>

                    {/* MORTGAGE */}
                    <div className="salary-box">
                      <div className="box-label">
                        MORTGAGE
                        <InfoTooltip text="Estimated monthly mortgage payment based on the selected home value, down payment, interest rate, and loan term." />
                      </div>

                      <div className="box-price">
                        $
                        {Number(monthlyMortgage.toFixed(0)).toLocaleString(
                          "en-US",
                        )}
                      </div>

                      <div className="box-subtext">
                        from ${(homePrice / 1000).toFixed(1)}K
                      </div>
                    </div>
                  </div>

                  {/* PROGRESS */}
                  <div className="afford-progress-grid">
                    {/* RENT */}
                    <div className="progress-card renting">
                      <div className="progress-top">
                        <span>
                          Renting · {rentPercent}%
                          <InfoTooltip text="City-median rent as a percentage of net pay." />
                        </span>

                        <strong>{rentPercent}%</strong>
                      </div>

                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${rentPercent}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* OWNING */}
                    {/* <div className="progress-card owning">
                      <div className="progress-top">
                        <span>Owning · {mortgagePercent}%</span>

                        <strong>{mortgagePercent}%</strong>
                      </div>

                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${mortgagePercent}%`,
                          }}
                        ></div>
                      </div>
                    </div> */}
                    <div
                      className={`progress-card owning ${
                        mortgagePercent <= 30
                          ? "safe-owning"
                          : mortgagePercent > 35
                          ? "danger-owning"
                          : ""
                      }`}
                    >
                      <div className="progress-top">
                        <span>
                          Owning · {mortgagePercent}%
                          <InfoTooltip text="Estimated mortgage as a percentage of net pay. 30% or under is safe; over 35% is a stretch." />
                        </span>

                        <strong>{mortgagePercent}%</strong>
                      </div>

                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${mortgagePercent}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  {Number(mortgagePercent) > 35 && (
                    <div className="down-payment-card">
                      <div className="down-titles">
                        Renting may be the better option at this salary.
                      </div>
                    </div>
                  )}

                  {/* DOWN PAYMENT */}
                  <div className="down-payment-card" data-tour="jd-down">
                    <div>
                      <div className="down-title">
                        DOWN PAYMENT NEEDED
                        <InfoTooltip text="The down-payment percentage from the mortgage details below, applied to the home price." />
                      </div>

                      <div className="down-subtitle">
                        {downPaymentPercent}% of $
                        {Number(homePrice).toLocaleString("en-US")}
                      </div>
                    </div>

                    <div className="down-price">
                      $
                      {Number(downPaymentAmount.toFixed(0)).toLocaleString(
                        "en-US",
                      )}
                    </div>
                  </div>

                  {/* BUTTON */}
                  <button
                    className="mortgage-btn"
                    data-tour="jd-calc-toggle"
                    onClick={() => {
                      if (!isAuthenticated && !showMortgageDetails) {
                        requireAuth(
                          "Log in or sign up to customize mortgage details.",
                        );
                        return;
                      }
                      setShowMortgageDetails(!showMortgageDetails);
                    }}
                  >
                    {showMortgageDetails
                      ? "Hide mortgage details"
                      : "Customize mortgage details"}

                    <span>
                      {showMortgageDetails ? (
                        <FiChevronUp />
                      ) : (
                        <FiChevronDown />
                      )}
                    </span>
                  </button>

                  {/* DETAILS */}
                  {showMortgageDetails && (
                    <div className="affordability-calculator-section reused-affordability-wrapper">
                      <div className="affordability-content">
                        <div className="calculator-form">
                          {/* HOME PRICE */}
                          <div className="form-group" data-tour="jd-price">
                            <label>Typical Home Value for the Region</label>
                            <div className="input-with-icon">
                              <span className="icon">$</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={
                                  Number(homePrice) > 0
                                    ? Number(homePrice).toLocaleString("en-US")
                                    : ""
                                }
                                placeholder="0"
                                maxLength={10}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                  setHomePrice(
                                    digits === "" ? 0 : Number(digits),
                                  );
                                }}
                              />
                            </div>
                          </div>

                          {/* DOWN PAYMENT */}
                          <div
                            className="form-group slider-group"
                            data-tour="jd-dp"
                          >
                            <div className="label-row">
                              <label>DOWN PAYMENT</label>
                              <span className="value">
                                {downPaymentPercent}% · $
                                {Number(
                                  downPaymentAmount.toFixed(0),
                                ).toLocaleString("en-US")}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="30"
                              value={downPaymentPercent}
                              className="custom-range"
                              onChange={(e) => {
                                setDownPaymentPercent(Number(e.target.value));
                                setHasUserChanged(true);
                              }}
                              style={{
                                "--val": `${(downPaymentPercent / 30) * 100}%`,
                              }}
                            />
                          </div>

                          {/* LOAN TERM */}
                          <div className="form-group" data-tour="jd-term">
                            <label>LOAN TERM</label>
                            <div className="term-tabs">
                              <div
                                className="term-slider"
                                style={termSliderStyle}
                                aria-hidden="true"
                              />
                              <button
                                ref={term15Ref}
                                type="button"
                                className={loanTerm === 15 ? "active" : ""}
                                onClick={() => setLoanTerm(15)}
                              >
                                15 yr
                              </button>
                              <button
                                ref={term30Ref}
                                type="button"
                                className={loanTerm === 30 ? "active" : ""}
                                onClick={() => setLoanTerm(30)}
                              >
                                30 yr
                              </button>
                            </div>
                          </div>

                          {/* INTEREST */}
                          <div className="form-group" data-tour="jd-rate">
                            <label>INTEREST RATE</label>
                            <div className="input-with-suffix">
                              <input
                                type="number"
                                step="0.01"
                                value={interestRate}
                                onChange={(e) =>
                                  setInterestRate(Number(e.target.value))
                                }
                              />
                              <span className="suffix">% APR</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          <div className="subscription-details" data-tour="jd-listings">
            {/* TABS */}
            <div className="subscription-tabs" data-tour="jd-mode">
              <div
                className="tab-slider"
                style={planSliderStyle}
                aria-hidden="true"
              />
              <button
                ref={forSaleBtnRef}
                type="button"
                className={`tab-pill ${
                  activePlan === "For Sale" ? "active" : ""
                }`}
                onClick={() => {
                  setActivePlan("For Sale");
                  setSelectedPlan("For Sale");
                }}
              >
                For Sale
              </button>
              <button
                ref={forRentBtnRef}
                type="button"
                className={`tab-pill ${
                  activePlan === "For Rent" ? "active" : ""
                }`}
                onClick={() => {
                  setActivePlan("For Rent");
                  setSelectedPlan("For Rent");
                }}
              >
                For Rent
              </button>
            </div>

            <div className="affordability-calculator-section reused-affordability-wrapper">
              <div className="affordability-content">
                {/* INFO CARDS */}
                <div className="results-cards">
                  {currentData?.cards?.map((item, index) => {
                    if (item.key === "utilities") {
                      return (
                        <div className="result-card utilities-card" key={index}>
                          <div className="label">
                            EXPENSES
                            <InfoTooltip text="Estimated monthly utilities and other living costs. Tap the amount to edit it." />
                          </div>
                          <div
                            className="value utilities-display"
                            onClick={openUtilitiesEditor}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openUtilitiesEditor();
                              }
                            }}
                          >
                            <span className="utilities-amount">
                              {item.value}
                            </span>
                            <Edit2
                              size={13}
                              className="utilities-edit-icon"
                              aria-label="Edit utilities"
                            />
                          </div>
                          <div className="suffix">{item.suffix}</div>
                        </div>
                      );
                    }
                    const isLeftover = String(item.label || "")
                      .toLowerCase()
                      .includes("leftover");
                    let valueModifier = "";
                    if (isLeftover && item.value) {
                      const numericPart = String(item.value).replace(
                        /[^0-9.\-]/g,
                        "",
                      );
                      const num = Number(numericPart);
                      if (!Number.isNaN(num)) {
                        valueModifier = num < 0 ? "red-val" : "green-val";
                      }
                    } else if (item.key === "mortgage" || index === 0) {
                      valueModifier = "blue-val";
                    }
                    const cardInfoText = {
                      mortgage:
                        "Estimated from the home price, down payment, interest rate, and loan term set above.",
                      rent: "City median monthly rent for this area.",
                      leftover:
                        activePlan === "For Rent"
                          ? "Net pay minus median rent and expenses."
                          : "Net pay minus the mortgage and expenses.",
                    }[item.key];
                    return (
                      <div className="result-card" key={index}>
                        <div
                          className="label"
                          style={{ display: "flex", alignItems: "flex-start" }}
                        >
                          <span>{item.label}</span>
                          {cardInfoText && <InfoTooltip text={cardInfoText} />}
                        </div>
                        <div className={`value ${valueModifier}`}>
                          {item.value || "--"}
                        </div>
                        {item.suffix && (
                          <div className="suffix">{item.suffix}</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* BUTTONS */}
                <div className="action-buttons">
                  <Button
                    className="redfin-btn"
                    data-tour="jd-redfin"
                    onClick={() =>
                      window.open(currentData.buttons.redfin.link, "_blank")
                    }
                  >
                    {currentData.buttons.redfin.label}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10 3.5h3.5v3.5M13.5 3.5l-6 6M6 3.5H2.5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V8" />
                    </svg>
                  </Button>

                  <Button
                    onClick={searchOnZillow}
                    className="zillow-btn"
                    data-tour="jd-zillow"
                  >
                    {activePlan === "For Rent"
                      ? "View homes for rent on Zillow"
                      : "View homes for sale on Zillow"}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M10 3.5h3.5v3.5M13.5 3.5l-6 6M6 3.5H2.5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V8" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="city-more-card city-more-card-flow-only"
            data-tour="jd-citycta"
          >
            <div className="city-more-title">
              More about {JobDetail?.city_details?.city_details?.city},{" "}
              {JobDetail?.city_details?.city_details?.state}
              <InfoTooltip text="Full city profile — demographics, schools, climate, and license requirements for nurses relocating here." />
            </div>

            <div className="city-more-description">
              Demographics, schools, climate, license requirements, and more
            </div>

            <button className="city-more-btn" onClick={handleCityDetailClick}>
              View {JobDetail?.city_details?.city_details?.city} city details
              <span>→</span>
            </button>
          </div>
          <div
            className="similar-jobs-section similar-jobs-flow-only"
            data-tour="jd-similar"
          >
            <div className="similar-title">
              Similar jobs
              <InfoTooltip text="Other open roles matching this job's nursing role and city." />
            </div>

            <div className="similar-subtitle">
              Other openings matched by role and city
            </div>

            {similarJobsLoading && similarPage === 1 ? (
              <div className="similar-jobs-loading">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : !similarJobs?.data?.length ? (
              <p className="no-jobs">No similar jobs found</p>
            ) : (
              <>
                <div className="similar-jobs-grid">
                  {similarJobs.data.map((job, i) => (
                    <div
                      className="similar-job-card"
                      data-tour={i === 0 ? "jd-similar0" : undefined}
                      key={job.id}
                      onClick={() => {
                        if (!job?.id) return;
                        if (!isAuthenticated) {
                          requireAuth(
                            "Log in or sign up to view this job's details.",
                          );
                          return;
                        }
                        if (jobViewLimitDataRef.current) {
                          setJobViewLimitInfo(jobViewLimitDataRef.current);
                          return;
                        }
                        navigate(`/job-detail/${job.id}`);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {/* LEFT */}
                      <div className="job-left">
                        <div className="job-logo">
                          <img
                            src={getSimilarAvatarSrc(job)}
                            alt={getSimilarCompany(job)}
                            className="user-avatar"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                getSimilarCompany(job),
                              )}&background=${getSimilarAvatarBg(
                                job,
                              )}&color=fff&size=100&bold=true`;
                            }}
                          />
                        </div>

                        <div className="job-content-card">
                          <div className="job-title">{job.title}</div>

                          <div className="job-hospital">
                            {getSimilarCompany(job)}
                          </div>

                          <div className="job-salary">
                            {formatSimilarSalary(job)}
                          </div>
                        </div>
                      </div>

                      {/* RIGHT */}
                      <div className="job-right">
                        <div className="leftover-price">
                          {formatSimilarLeftover(job)}
                        </div>

                        {hasSimilarSalary(job) && (
                          <div className="leftover-label">
                            AFTER HOUSING /MO
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {((similarJobs?.page || 1) < (similarJobs?.pages || 1) ||
                    (similarJobs?.data?.length || 0) <
                      (similarJobs?.total || 0)) && (
                    <div className="similar-show-more-wrap">
                      <Button
                        className="similar-show-more-btn"
                        onClick={handleLoadMoreSimilar}
                        disabled={similarJobsLoading}
                      >
                        {similarJobsLoading ? "Loading…" : "Show more"}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {/* MOBILE STICKY APPLY */}

          <div
            className={`mobile-apply-bar ${showStickyApply ? "show" : ""}`}
            data-tour="jd-sticky"
          >
            <Button
              className="apply-btn"
              // onClick={() => {
              //   if (!JobDetail?.id) return;

              //   if (JobDetail?.source === "hospital") {
              //     navigate(`/job-detail/${JobDetail.id}`);
              //     return;
              //   }

              //   // dispatch(applyJob(JobDetail.id));
              // }}
              onClick={async () => {
                if (!JobDetail?.id) return;

                try {
                  const session_id = sessionStorage.getItem(
                    "comparison_session_id",
                  );
                  if (session_id) {
                    // 🔥 Track application click
                    dispatch(
                      jobTracking({
                        session_id,
                        event_type: "application_clicked",
                        job_id: JobDetail.id,
                        job_title: JobDetail?.title || null,
                        specialty: selectedRole || null,
                        job_type: selectedJobType?.[0] || null,
                        shift_type: selectedShiftType?.[0] || null,
                        company_name:
                          JobDetail?.company_name ||
                          JobDetail?.company?.name ||
                          null,
                        timestamp: new Date().toISOString(),
                      }),
                    );
                  }

                  // 🔥 Call apply API
                  // await dispatch(applyJob(JobDetail.id)).unwrap();

                  // 🔥 Open application URL
                  const url = getApplyUrl(JobDetail?.application_url);
                  if (url && url !== "#") {
                    // Increment the visible "people clicked apply" counter
                    // (authenticated-only endpoint — skip for guests).
                    if (isAuthenticated) {
                      jobsService
                        .trackApplyClick(JobDetail.id)
                        .catch(() => {});
                    }
                    window.open(url, "_blank", "noopener,noreferrer");
                  }
                } catch (error) {
                  console.error("Apply failed:", error);
                }
              }}
            >
              Apply Now
            </Button>
          </div>
        </div>
      </div>

      {/* Data source disclaimer — placed OUTSIDE the .job-details-page grid
          so it's a normal full-width block below the whole layout. Keeping it
          inside the grid let the sticky right rail (grid rows 2–3) overlap it;
          out here the grid (and its sticky rail) is fully contained above. */}
      <div className="zillow-data-disclaimer">
        <div className="zillow-data-disclaimer__title">Data Disclaimer</div>
        {/* <p>
          Housing data sourced from{" "}
          <a
            href="https://www.zillow.com/research/data/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Zillow Research
          </a>
          .
        </p> */}
        <p>
          Salary figures on this page are based on the employer's posted pay
          range. City-level wage comparisons come from the U.S. Bureau of Labor
          Statistics and housing data from Zillow Research. Figures represent
          typical values for the region and are refreshed periodically. They may
          not reflect real-time market conditions, the final offer for this
          position, or your individual circumstances.
        </p>
      </div>

      <RestrictionModal
        isVisible={showUtilitiesUpgradeModal}
        onClose={() => setShowUtilitiesUpgradeModal(false)}
        eventSource="job_detail_utilities"
        iconType="wallet"
        thirdCtaMode="newsletter"
        heading="Want to add expenses for this city?"
        subText="Upgrade to track your expenses and get personalized insights."
        infoText="Pro users can add expenses, track budgets, and analyze cost of living in any city."
      />

      <RestrictionModal
        isVisible={showSaveJobModal}
        onClose={() => setShowSaveJobModal(false)}
        eventSource="save_job"
        iconType="clipboard-dollar"
        thirdCtaMode="newsletter"
        heading="Save more jobs"
        subText="Free users can save 1 job per day. Upgrade to Pro for unlimited saved jobs."
        infoText="Pro users can save unlimited jobs, organize opportunities, and track applications."
      />

      <RestrictionModal
        isVisible={Boolean(jobViewLimitInfo)}
        onClose={() => setJobViewLimitInfo(null)}
        eventSource="job_view_limit"
        iconType="briefcase"
        heading="You've reached your daily job-view limit"
        subText={`You've viewed your ${jobViewLimitInfo?.limit ??
          5} free jobs for today. Upgrade to Pro for unlimited job views.`}
        infoText={`You're on the ${(jobViewLimitInfo?.planSlug || "free")
          .charAt(0)
          .toUpperCase() +
          (jobViewLimitInfo?.planSlug || "free").slice(
            1,
          )} plan · ${jobViewLimitInfo?.used ??
          0} of ${jobViewLimitInfo?.limit ?? 5} job views used today`}
        thirdCtaMode="newsletter"
      />

      <Modal
        show={showUtilitiesEditModal}
        onHide={() => setShowUtilitiesEditModal(false)}
        centered
        className="utilities-edit-modal"
        dialogClassName="utilities-edit-dialog"
        contentClassName="utilities-edit-content"
        backdropClassName="utilities-edit-backdrop"
      >
        <Modal.Body className="utilities-edit-body">
          <button
            type="button"
            className="utilities-edit-close"
            aria-label="Close"
            onClick={() => setShowUtilitiesEditModal(false)}
          >
            <FiX size={16} />
          </button>

          <div className="utilities-edit-header">
            <div className="utilities-edit-icon-badge">
              <CalculatorIcon />
            </div>
            <div className="utilities-edit-heading">
              <h5 className="utilities-edit-title">Monthly expenses</h5>
              <p className="utilities-edit-subtitle">
                Electricity, water, gas &amp; internet.
              </p>
            </div>
          </div>

          <div className="utilities-edit-input-wrap">
            <span className="utilities-edit-prefix">$</span>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="utilities-edit-input"
              value={utilitiesDraft}
              autoFocus
              onChange={(e) => setUtilitiesDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveUtilitiesDraft();
              }}
            />
            <span className="utilities-edit-suffix">/month</span>
          </div>

          <div className="utilities-edit-presets">
            {[300, 500, 750, 1000].map((preset) => {
              const active = Number(utilitiesDraft) === preset;
              return (
                <button
                  type="button"
                  key={preset}
                  className={`utilities-preset-chip ${
                    active ? "is-active" : ""
                  }`}
                  onClick={() => setUtilitiesDraft(String(preset))}
                >
                  ${preset}
                </button>
              );
            })}
          </div>

          <div className="utilities-edit-actions">
            <button
              type="button"
              className="utilities-edit-btn utilities-edit-btn-secondary"
              onClick={() => setShowUtilitiesEditModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="utilities-edit-btn utilities-edit-btn-primary"
              onClick={saveUtilitiesDraft}
            >
              Save changes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <CityDetailLimitModal
        isVisible={cityLimitModalVisible}
        onClose={() => setCityLimitModalVisible(false)}
        eventSource={upgradeSource}
        heading={cityLimitModalData.heading}
        infoText={cityLimitModalData.infoText}
      />

      <AuthRequiredModal
        isVisible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        subText={authModalText}
      />

      <GuidedTour
        isOpen={isTourOpen}
        steps={jobDetailTourSteps}
        onDone={finishTour}
      />
    </>
  );
};

export default JobDetail;
